/**
 * Yamiward — BLOODLINES: KESSEN 決戦
 * The Arena. Phase 1: deterministic fight engine, local play + AI.
 *
 * ARCHITECTURE NOTE — this file is written for rollback netcode (Phase 2).
 * The simulation is a fixed 60Hz integer-only state machine. Every value that
 * affects the outcome of a fight lives in Sim state and is reproducible from
 * (seed, input history) alone:
 *   - no Math.random in the sim (seeded xorshift lives in state)
 *   - no floats in the sim (positions are hundredths of a logical pixel)
 *   - no wall-clock reads in the sim (the tick counter is the only time)
 *   - rendering NEVER writes to sim state
 * Sim.tick(state, [p1mask, p2mask]) / Sim.serialize / Sim.deserialize are the
 * three functions a rollback session needs. Do not break that contract.
 */

document.addEventListener("DOMContentLoaded", () => {
    const root = document.getElementById("arena");
    if (!root) return;

    // ==================================================================
    // FIXED-POINT + DETERMINISTIC RNG
    // ==================================================================

    const S = 100;                 // sub-units per logical pixel
    const px = (v) => v * S;
    const idiv = (a, b) => (a / b) | 0;   // truncates toward zero, exact < 2^31

    function rngNext(state) {       // xorshift32 — returns [value, newState]
        let x = state | 0;
        x ^= x << 13; x |= 0;
        x ^= x >>> 17;
        x ^= x << 5; x |= 0;
        return x | 0;
    }

    // ==================================================================
    // INPUT BITMASK
    // ==================================================================

    const IN = { LEFT: 1, RIGHT: 2, UP: 4, DOWN: 8, LIGHT: 16, HEAVY: 32, SPECIAL: 64 };

    // ==================================================================
    // FIGHTER STATES
    // ==================================================================

    const ST = {
        IDLE: 0, WALK_F: 1, WALK_B: 2, CROUCH: 3, JUMP: 4,
        LIGHT: 5, HEAVY: 6, SPECIAL: 7,
        BLOCK: 8, BLOCK_LOW: 9, HITSTUN: 10, HITSTUN_AIR: 11,
        KNOCKDOWN: 12, WAKEUP: 13, DASH: 14, BACKDASH: 15,
        WIN: 16, LOSE: 17, INTRO: 18
    };

    const GUARD = { MID: 0, HIGH: 1, LOW: 2 };

    // ==================================================================
    // ROSTER — all twelve bloodlines, one fighter per month.
    // Frame data is in ticks at 60Hz. Distances are logical pixels.
    // Every number here is balance surface; the AI-vs-AI sim harness
    // (Sim.simulate) exists so these can be tuned against 10k matches.
    // INDEX ORDER IS A NETWORK CONTRACT — matchmaking sends roster indexes,
    // so entries are append-only: oni and raiju keep 0 and 1 forever.
    //
    // Per-fighter identity comes from small generic knobs the sim reads:
    //   armorFrom/armorTo + armorCost  — hyper armor window (oni, kodama, ryu)
    //   unblockableClose               — command grab (oni only)
    //   guard: HIGH / LOW              — overheads and lows (mixup characters)
    //   invulnFrom/invulnTo            — strike-invuln window (kitsune)
    //   leech / selfCost               — lifesteal at a blood price (hitodama)
    //   regen                          — grounded-neutral health regrowth (kodama)
    //   meterRegen                     — passive ki, ticks per point (ameonna)
    //   blockMeter                     — bonus ki for blocking (bakeneko)
    // plus raiju's bespoke CHARGE stacks, which predate the knob system.
    // ==================================================================

    const ROSTER = [
        {
            key: "oni", name: "ONI", kanji: "鬼", romaji: "oni", month: 2,
            color: "#E51E25", color2: "#FF6B4A",
            archetype: "GRAPPLER",
            blurb: "The February bloodline. Slow, immovable, and catastrophic on contact. Armor carries him through your jab; a whiffed swing carries him into your combo.",
            strength: "Super armor on heavy. Highest damage in the ward.",
            weakness: "Slowest walk and worst recovery — whiffs are free punishes.",
            stats: { POW: 9, SPD: 3, DEF: 8, KI: 5 },
            health: 1050,
            walkF: 245, walkB: 150, dashSpd: 760, dashDur: 15,
            jumpV: 1750, gravity: 95, airDrift: 100,
            weight: 88,                     // higher = less knockback taken
            armorCost: 70,                  // % of damage still taken through armour
            attacks: {
                // The grappler has the SHORTEST normals in the ward. He is paid in
                // damage and armor for the work of getting inside.
                light: { startup: 5, active: 3, recovery: 11, dmg: 46, guard: GUARD.MID, hitstun: 16, blockstun: 9, kb: 240, kbUp: 0, box: [42, 74, 52, 34], hitstop: 5, meter: 7, cancel: "heavy" },
                // Armor covers only the middle of startup: enough to walk through
                // a poke you read, not enough to make the button free.
                heavy: { startup: 13, active: 4, recovery: 25, dmg: 92, guard: GUARD.MID, hitstun: 24, blockstun: 13, kb: 620, kbUp: 260, box: [36, 52, 86, 64], hitstop: 9, meter: 13, cancel: "special", armorFrom: 7, armorTo: 13 },
                // The grab that beats guard — and the whiff that loses the round.
                special: { startup: 13, active: 5, recovery: 40, dmg: 148, guard: GUARD.MID, hitstun: 34, blockstun: 18, kb: 900, kbUp: 620, box: [30, 40, 78, 92], hitstop: 14, meter: 0, cost: 50, unblockableClose: 52, rush: 620, launch: 1, name: "CRUSHING GRIP" }
            }
        },
        {
            key: "raiju", name: "RAIJU", kanji: "雷獣", romaji: "raijū", month: 8,
            color: "#A06AE8", color2: "#D9B3FF",
            archetype: "RUSHDOWN",
            blurb: "The August bloodline. Every clean hit feeds the charge and the charge feeds the speed. Lose the momentum and the beast is just a thin silhouette in the rain.",
            strength: "Fastest in the ward. Landed hits stack CHARGE for more speed.",
            weakness: "Fragile. Blocked or whiffed attacks burn the charge away.",
            stats: { POW: 5, SPD: 9, DEF: 4, KI: 7 },
            health: 1010,
            walkF: 375, walkB: 255, dashSpd: 900, dashDur: 12,
            jumpV: 1680, gravity: 82, airDrift: 190,
            weight: 62,
            attacks: {
                light: { startup: 3, active: 2, recovery: 8, dmg: 34, guard: GUARD.MID, hitstun: 15, blockstun: 9, kb: 180, kbUp: 0, box: [44, 78, 56, 28], hitstop: 4, meter: 8, cancel: "heavy" },
                heavy: { startup: 8, active: 3, recovery: 16, dmg: 80, guard: GUARD.MID, hitstun: 21, blockstun: 12, kb: 460, kbUp: 300, box: [40, 46, 86, 52], hitstop: 7, meter: 12, cancel: "special" },
                special: { startup: 7, active: 6, recovery: 22, dmg: 132, guard: GUARD.MID, hitstun: 28, blockstun: 15, kb: 700, kbUp: 380, box: [26, 56, 96, 60], hitstop: 11, meter: 0, cost: 50, rush: 1180, launch: 1, name: "THUNDER RUSH" }
            }
        },
        {
            key: "yukionna", name: "YUKIONNA", kanji: "雪女", romaji: "yuki-onna", month: 1,
            color: "#DFE9F2", color2: "#9CD8FF",
            archetype: "SPELLBINDER",
            blurb: "The January bloodline. She gives ground gracefully and takes whole winters back. One clean FIRST FROST and your health bar belongs to the follow-up.",
            strength: "FIRST FROST freezes on hit — a landed special is a free combo.",
            weakness: "Below-average damage until the freeze actually lands.",
            stats: { POW: 4, SPD: 5, DEF: 6, KI: 9 },
            health: 1050,
            walkF: 290, walkB: 240, dashSpd: 780, dashDur: 13,
            jumpV: 1700, gravity: 86, airDrift: 140,
            weight: 66,
            attacks: {
                light: { startup: 4, active: 2, recovery: 10, dmg: 42, guard: GUARD.MID, hitstun: 15, blockstun: 9, kb: 200, kbUp: 0, box: [46, 76, 58, 28], hitstop: 4, meter: 8, cancel: "heavy" },
                // The overhead: crouch-blockers eat it. The box reaches low
                // enough to touch a croucher — the mixup is in the guard flag,
                // not in whiffing over their head.
                heavy: { startup: 9, active: 3, recovery: 17, dmg: 88, guard: GUARD.HIGH, hitstun: 22, blockstun: 12, kb: 430, kbUp: 280, box: [42, 60, 82, 54], hitstop: 8, meter: 12, cancel: "special" },
                // Freeze = huge hitstun, almost no knockback: they stand there.
                // The longest special poke in the ward — winter reaches you first.
                special: { startup: 10, active: 4, recovery: 22, dmg: 92, guard: GUARD.MID, hitstun: 56, blockstun: 17, kb: 90, kbUp: 0, box: [36, 44, 98, 80], hitstop: 12, meter: 0, cost: 50, name: "FIRST FROST" }
            }
        },
        {
            key: "kitsune", name: "KITSUNE", kanji: "狐", romaji: "kitsune", month: 3,
            color: "#E8A33D", color2: "#FFD9A0",
            archetype: "TRICKSTER",
            blurb: "The March bloodline. Nine tails, zero honest angles. The feint slips straight through your answer and arrives as the question.",
            strength: "NINE-TAIL FEINT is invincible through its startup.",
            weakness: "Second-lowest health — guess wrong twice and it's over.",
            stats: { POW: 5, SPD: 8, DEF: 3, KI: 8 },
            health: 1020,
            walkF: 350, walkB: 245, dashSpd: 880, dashDur: 12,
            jumpV: 1720, gravity: 84, airDrift: 180,
            weight: 58,
            attacks: {
                light: { startup: 3, active: 2, recovery: 8, dmg: 36, guard: GUARD.MID, hitstun: 15, blockstun: 9, kb: 190, kbUp: 0, box: [44, 78, 56, 28], hitstop: 4, meter: 8, cancel: "heavy" },
                heavy: { startup: 9, active: 3, recovery: 17, dmg: 86, guard: GUARD.MID, hitstun: 21, blockstun: 12, kb: 440, kbUp: 300, box: [40, 48, 84, 52], hitstop: 7, meter: 12, cancel: "special" },
                special: { startup: 8, active: 5, recovery: 26, dmg: 132, guard: GUARD.MID, hitstun: 27, blockstun: 14, kb: 680, kbUp: 360, box: [24, 52, 92, 58], hitstop: 10, meter: 0, cost: 50, rush: 1050, launch: 1, invulnFrom: 2, invulnTo: 10, name: "NINE-TAIL FEINT" }
            }
        },
        {
            key: "kodama", name: "KODAMA", kanji: "木霊", romaji: "kodama", month: 4,
            color: "#9FD98A", color2: "#D6F5C4",
            archetype: "WARDEN",
            blurb: "The April bloodline. The tree does not chase. It waits, takes your best swing on the bark, and answers with the whole forest.",
            strength: "Regrows health while standing his ground. Armored heavy.",
            weakness: "Slow hands — relentless pressure never lets the bark heal.",
            stats: { POW: 6, SPD: 3, DEF: 9, KI: 6 },
            health: 1100,
            walkF: 265, walkB: 175, dashSpd: 740, dashDur: 14,
            jumpV: 1720, gravity: 92, airDrift: 110,
            weight: 82,
            regen: 3,
            armorCost: 70,
            attacks: {
                light: { startup: 4, active: 3, recovery: 11, dmg: 42, guard: GUARD.MID, hitstun: 16, blockstun: 9, kb: 230, kbUp: 0, box: [42, 74, 54, 32], hitstop: 5, meter: 7, cancel: "heavy" },
                heavy: { startup: 10, active: 4, recovery: 22, dmg: 100, guard: GUARD.MID, hitstun: 23, blockstun: 13, kb: 560, kbUp: 240, box: [36, 50, 88, 60], hitstop: 9, meter: 13, cancel: "special", armorFrom: 5, armorTo: 11 },
                special: { startup: 10, active: 4, recovery: 28, dmg: 148, guard: GUARD.MID, hitstun: 30, blockstun: 16, kb: 760, kbUp: 520, box: [30, 42, 80, 84], hitstop: 12, meter: 0, cost: 50, launch: 1, name: "ECHO ROOT" }
            }
        },
        {
            key: "kappa", name: "KAPPA", kanji: "河童", romaji: "kappa", month: 5,
            color: "#3AA79B", color2: "#8FE3D5",
            archetype: "TECHNICAL",
            blurb: "The May bloodline. River discipline: every answer in the book, none of them flashy. The slide comes in under your guard and asks you to bow.",
            strength: "Honest all-rounder. RIVER RULE hits low and launches.",
            weakness: "No standout stat — wins on decisions, not gifts.",
            stats: { POW: 6, SPD: 6, DEF: 6, KI: 6 },
            health: 1040,
            walkF: 300, walkB: 210, dashSpd: 820, dashDur: 13,
            jumpV: 1730, gravity: 88, airDrift: 150,
            weight: 72,
            attacks: {
                light: { startup: 4, active: 2, recovery: 9, dmg: 38, guard: GUARD.MID, hitstun: 15, blockstun: 9, kb: 210, kbUp: 0, box: [44, 76, 56, 28], hitstop: 4, meter: 8, cancel: "heavy" },
                heavy: { startup: 10, active: 3, recovery: 18, dmg: 84, guard: GUARD.MID, hitstun: 21, blockstun: 12, kb: 480, kbUp: 300, box: [40, 48, 86, 52], hitstop: 7, meter: 12, cancel: "special" },
                // The slide: a LOW — stand-blockers get swept.
                special: { startup: 9, active: 6, recovery: 24, dmg: 112, guard: GUARD.LOW, hitstun: 28, blockstun: 15, kb: 640, kbUp: 420, box: [26, 4, 96, 44], hitstop: 11, meter: 0, cost: 50, rush: 980, launch: 1, name: "RIVER RULE" }
            }
        },
        {
            key: "ameonna", name: "AMEONNA", kanji: "雨女", romaji: "ame-onna", month: 6,
            color: "#8A9BB8", color2: "#C9D6EE",
            archetype: "STORMCALLER",
            blurb: "The June bloodline. The rain falls whether you swing or not — her ki fills the same way. Stall, and the ledger only grows.",
            strength: "Ki charges itself. The special is nearly always loaded.",
            weakness: "Ordinary buttons — take the rain away and she's plain.",
            stats: { POW: 5, SPD: 5, DEF: 5, KI: 9 },
            health: 1030,
            walkF: 300, walkB: 210, dashSpd: 800, dashDur: 13,
            jumpV: 1700, gravity: 87, airDrift: 150,
            weight: 68,
            meterRegen: 20,
            attacks: {
                light: { startup: 4, active: 2, recovery: 10, dmg: 38, guard: GUARD.MID, hitstun: 15, blockstun: 9, kb: 200, kbUp: 0, box: [44, 76, 56, 28], hitstop: 4, meter: 7, cancel: "heavy" },
                heavy: { startup: 10, active: 3, recovery: 19, dmg: 92, guard: GUARD.MID, hitstun: 21, blockstun: 12, kb: 460, kbUp: 280, box: [40, 48, 88, 52], hitstop: 7, meter: 11, cancel: "special" },
                special: { startup: 9, active: 5, recovery: 28, dmg: 164, guard: GUARD.MID, hitstun: 30, blockstun: 16, kb: 780, kbUp: 520, box: [28, 44, 92, 72], hitstop: 13, meter: 0, cost: 50, launch: 1, name: "DOWNPOUR LEDGER" }
            }
        },
        {
            key: "hitodama", name: "HITODAMA", kanji: "人魂", romaji: "hitodama", month: 7,
            color: "#9BE8E0", color2: "#D9FFFB",
            archetype: "GLASS FLAME",
            blurb: "The July bloodline. A soul-flame burning at both ends: the siphon pays its cost in her own fire and settles the difference from yours.",
            strength: "SOUL SIPHON drains your health into hers.",
            weakness: "Lowest health in the ward — and the siphon costs some of it.",
            stats: { POW: 7, SPD: 7, DEF: 2, KI: 8 },
            health: 930,
            walkF: 340, walkB: 240, dashSpd: 870, dashDur: 12,
            jumpV: 1760, gravity: 80, airDrift: 200,
            weight: 56,
            attacks: {
                light: { startup: 3, active: 2, recovery: 9, dmg: 36, guard: GUARD.MID, hitstun: 15, blockstun: 9, kb: 190, kbUp: 0, box: [44, 78, 56, 28], hitstop: 4, meter: 9, cancel: "heavy" },
                heavy: { startup: 8, active: 3, recovery: 17, dmg: 80, guard: GUARD.MID, hitstun: 21, blockstun: 12, kb: 450, kbUp: 300, box: [40, 46, 84, 52], hitstop: 7, meter: 13, cancel: "special" },
                special: { startup: 9, active: 5, recovery: 26, dmg: 138, guard: GUARD.MID, hitstun: 28, blockstun: 15, kb: 660, kbUp: 380, box: [26, 50, 92, 60], hitstop: 12, meter: 0, cost: 50, rush: 900, launch: 1, selfCost: 22, leech: 65, name: "SOUL SIPHON" }
            }
        },
        {
            key: "tsukiusagi", name: "TSUKIUSAGI", kanji: "月兎", romaji: "tsuki-usagi", month: 9,
            color: "#C9CDD6", color2: "#F4F6FA",
            archetype: "PRECISION",
            blurb: "The September bloodline. The rabbit has pounded the same mochi for ten thousand years. Every strike lands exactly where the last one did — on you.",
            strength: "Fastest buttons after Raiju, and every touch builds ki.",
            weakness: "Light damage — needs three openings where others need one.",
            stats: { POW: 4, SPD: 8, DEF: 4, KI: 8 },
            health: 980,
            walkF: 330, walkB: 235, dashSpd: 860, dashDur: 12,
            jumpV: 1780, gravity: 84, airDrift: 180,
            weight: 60,
            attacks: {
                light: { startup: 3, active: 2, recovery: 7, dmg: 36, guard: GUARD.MID, hitstun: 15, blockstun: 9, kb: 180, kbUp: 0, box: [44, 78, 56, 28], hitstop: 4, meter: 11, cancel: "heavy" },
                heavy: { startup: 7, active: 3, recovery: 13, dmg: 78, guard: GUARD.MID, hitstun: 21, blockstun: 12, kb: 430, kbUp: 300, box: [42, 48, 90, 52], hitstop: 7, meter: 16, cancel: "special" },
                // The mallet comes straight down: an overhead launcher.
                special: { startup: 10, active: 4, recovery: 24, dmg: 140, guard: GUARD.HIGH, hitstun: 28, blockstun: 15, kb: 620, kbUp: 560, box: [30, 60, 84, 72], hitstop: 11, meter: 0, cost: 50, launch: 1, name: "FULL MOON MALLET" }
            }
        },
        {
            key: "bakeneko", name: "BAKENEKO", kanji: "化け猫", romaji: "bakeneko", month: 10,
            color: "#E07B39", color2: "#FFC08A",
            archetype: "COUNTER",
            blurb: "The October bloodline. She watched you decide before you did. Blocking isn't defence to the cat — it's the part where you feed her.",
            strength: "Blocking builds her ki fast. The paw sweep hits low.",
            weakness: "Has to take pressure to profit from it.",
            stats: { POW: 5, SPD: 8, DEF: 5, KI: 7 },
            health: 990,
            walkF: 345, walkB: 250, dashSpd: 880, dashDur: 12,
            jumpV: 1740, gravity: 83, airDrift: 185,
            weight: 62,
            blockMeter: 6,
            attacks: {
                // The paw sweep: a LOW jab. Nobody else has one on a light.
                light: { startup: 4, active: 2, recovery: 11, dmg: 30, guard: GUARD.LOW, hitstun: 15, blockstun: 9, kb: 200, kbUp: 0, box: [42, 6, 58, 30], hitstop: 4, meter: 8, cancel: "heavy" },
                heavy: { startup: 9, active: 3, recovery: 17, dmg: 78, guard: GUARD.MID, hitstun: 21, blockstun: 12, kb: 460, kbUp: 320, box: [40, 46, 84, 52], hitstop: 7, meter: 12, cancel: "special" },
                special: { startup: 8, active: 5, recovery: 24, dmg: 110, guard: GUARD.MID, hitstun: 27, blockstun: 14, kb: 680, kbUp: 400, box: [24, 50, 94, 58], hitstop: 11, meter: 0, cost: 50, rush: 1020, launch: 1, name: "SECOND SHADOW" }
            }
        },
        {
            key: "tengu", name: "TENGU", kanji: "天狗", romaji: "tengu", month: 11,
            color: "#CF4A2F", color2: "#FFA07A",
            archetype: "FOOTSIES",
            blurb: "The November bloodline. The mountain crow has never been wrong about distance. His reach ends exactly where your options do.",
            strength: "Longest normals in the ward — owns the whole midrange.",
            weakness: "Up close the wings are just a coat. Fragile for his size.",
            stats: { POW: 7, SPD: 6, DEF: 4, KI: 6 },
            health: 950,
            walkF: 305, walkB: 235, dashSpd: 840, dashDur: 13,
            jumpV: 1750, gravity: 85, airDrift: 170,
            weight: 66,
            attacks: {
                light: { startup: 5, active: 3, recovery: 13, dmg: 36, guard: GUARD.MID, hitstun: 16, blockstun: 9, kb: 260, kbUp: 0, box: [50, 76, 62, 26], hitstop: 5, meter: 8, cancel: "heavy" },
                heavy: { startup: 11, active: 3, recovery: 23, dmg: 76, guard: GUARD.MID, hitstun: 22, blockstun: 13, kb: 540, kbUp: 320, box: [46, 50, 90, 50], hitstop: 8, meter: 13, cancel: "special" },
                special: { startup: 10, active: 5, recovery: 27, dmg: 120, guard: GUARD.MID, hitstun: 30, blockstun: 16, kb: 720, kbUp: 640, box: [32, 48, 96, 80], hitstop: 12, meter: 0, cost: 50, rush: 820, launch: 1, name: "MOUNTAIN GALE" }
            }
        },
        {
            key: "ryu", name: "RYU", kanji: "龍", romaji: "ryū", month: 12,
            color: "#D8B45A", color2: "#F2E2A6",
            archetype: "SOVEREIGN",
            blurb: "The December bloodline. The dragon does not rush the year's end — it arrives, and the year ends. The last bell hits exactly once.",
            strength: "Most health in the ward. YEAR'S END is armored and enormous.",
            weakness: "Slowest walk — pays for the crown with his feet.",
            stats: { POW: 9, SPD: 2, DEF: 9, KI: 4 },
            health: 1200,
            walkF: 270, walkB: 170, dashSpd: 740, dashDur: 15,
            jumpV: 1740, gravity: 96, airDrift: 95,
            weight: 92,
            armorCost: 55,
            attacks: {
                light: { startup: 4, active: 3, recovery: 12, dmg: 46, guard: GUARD.MID, hitstun: 16, blockstun: 9, kb: 300, kbUp: 0, box: [44, 74, 56, 34], hitstop: 5, meter: 7, cancel: "heavy" },
                heavy: { startup: 11, active: 4, recovery: 24, dmg: 108, guard: GUARD.MID, hitstun: 24, blockstun: 13, kb: 600, kbUp: 280, box: [38, 50, 96, 62], hitstop: 9, meter: 13, cancel: "special" },
                special: { startup: 14, active: 5, recovery: 30, dmg: 172, guard: GUARD.MID, hitstun: 32, blockstun: 18, kb: 860, kbUp: 600, box: [30, 42, 88, 88], hitstop: 14, meter: 0, cost: 50, launch: 1, armorFrom: 6, armorTo: 14, name: "YEAR'S END" }
            }
        }
    ];

    const CHAR_BY_KEY = {};
    ROSTER.forEach((c, i) => { c.index = i; CHAR_BY_KEY[c.key] = c; });

    // ==================================================================
    // STAGE / GLOBAL SIM CONSTANTS
    // ==================================================================

    const STAGE_HALF = px(500);
    const GROUND_Y = 0;
    const PUSH_RADIUS = px(38);      // body-to-body pushbox
    const ROUND_TICKS = 60 * 60;     // 60 second rounds
    const ROUNDS_TO_WIN = 2;
    const HURT_W = px(58), HURT_H = px(148), CROUCH_H = px(96);
    const MAX_METER = 100;
    const MAX_CHARGE = 5;
    const CHARGE_DECAY = 200;        // ticks per charge lost when idle

    const RS = { INTRO: 0, FIGHT: 1, KO: 2, ROUND_END: 3, MATCH_END: 4 };

    // ==================================================================
    // SIM — the deterministic core.
    // ==================================================================

    const Sim = {
        create(seed, charA, charB) {
            const mk = (ci, side) => ({
                ci: ci,
                x: side < 0 ? -px(190) : px(190),
                y: 0, vx: 0, vy: 0,
                facing: side < 0 ? 1 : -1,
                state: ST.INTRO, sf: 0,
                health: ROSTER[ci].health,
                meter: 0, charge: 0, chargeTimer: 0,
                stun: 0, combo: 0, comboDmg: 0,
                hitUsed: 0,       // attack already connected this swing
                canCancel: 0,
                invuln: 0, armor: 0, armorUsed: 0,
                airborne: 0, juggle: 0,
                dashBuf: 0, dashDir: 0,
                lastMask: 0,
                wins: 0
            });
            return {
                tick: 0,
                rng: (seed | 0) || 0x5eed1234,
                timer: ROUND_TICKS,
                round: 1,
                roundState: RS.INTRO,
                rsFrame: 0,
                hitstop: 0,
                shake: 0,
                winner: 0,
                p: [mk(charA, -1), mk(charB, 1)],
                fx: []                          // deterministic effect spawns
            };
        },

        clone(s) {
            return {
                tick: s.tick, rng: s.rng, timer: s.timer, round: s.round,
                roundState: s.roundState, rsFrame: s.rsFrame, hitstop: s.hitstop,
                shake: s.shake, winner: s.winner,
                p: [Object.assign({}, s.p[0]), Object.assign({}, s.p[1])],
                fx: s.fx.map((f) => Object.assign({}, f))
            };
        },

        // NetplayJS-compatible snapshot pair. Effects are cosmetic but are
        // included so a rolled-back frame renders identically on both peers.
        serialize(s) {
            const f = [];
            ["tick", "rng", "timer", "round", "roundState", "rsFrame", "hitstop", "shake", "winner"].forEach((k) => f.push(s[k]));
            for (const pl of s.p) {
                ["ci", "x", "y", "vx", "vy", "facing", "state", "sf", "health", "meter",
                    "charge", "chargeTimer", "stun", "combo", "comboDmg", "hitUsed", "canCancel",
                    "invuln", "armor", "armorUsed", "airborne", "juggle", "dashBuf", "dashDir", "lastMask", "wins"].forEach((k) => f.push(pl[k]));
            }
            return f;
        },

        deserialize(s, f) {
            let i = 0;
            ["tick", "rng", "timer", "round", "roundState", "rsFrame", "hitstop", "shake", "winner"].forEach((k) => { s[k] = f[i++]; });
            for (const pl of s.p) {
                ["ci", "x", "y", "vx", "vy", "facing", "state", "sf", "health", "meter",
                    "charge", "chargeTimer", "stun", "combo", "comboDmg", "hitUsed", "canCancel",
                    "invuln", "armor", "armorUsed", "airborne", "juggle", "dashBuf", "dashDir", "lastMask", "wins"].forEach((k) => { pl[k] = f[i++]; });
            }
            return s;
        },

        rand(s, n) { s.rng = rngNext(s.rng); return ((s.rng >>> 8) % n + n) % n; },

        def(pl) { return ROSTER[pl.ci]; },

        spawnFx(s, kind, x, y, color, dir) {
            if (s.fx.length > 24) s.fx.shift();
            s.fx.push({ kind: kind, x: x, y: y, life: 0, dir: dir || 1, color: color || 0 });
        },

        // ---- the tick ------------------------------------------------

        tick(s, masks) {
            s.tick++;

            // Hitstop freezes the fight but not the clock on effects — this is
            // what makes a heavy connect feel like it weighs something.
            if (s.hitstop > 0) {
                s.hitstop--;
                for (const f of s.fx) f.life++;
                if (s.shake > 0) s.shake--;
                return s;
            }
            if (s.shake > 0) s.shake--;
            for (let i = s.fx.length - 1; i >= 0; i--) { s.fx[i].life++; if (s.fx[i].life > 30) s.fx.splice(i, 1); }

            if (s.roundState === RS.INTRO) {
                s.rsFrame++;
                if (s.rsFrame > 100) { s.roundState = RS.FIGHT; s.rsFrame = 0; s.p[0].state = ST.IDLE; s.p[1].state = ST.IDLE; s.p[0].sf = 0; s.p[1].sf = 0; }
                return s;
            }

            if (s.roundState === RS.KO || s.roundState === RS.ROUND_END) {
                s.rsFrame++;
                // let bodies settle
                for (let i = 0; i < 2; i++) this.physics(s, s.p[i], 0);
                this.pushApart(s);
                if (s.roundState === RS.KO && s.rsFrame > 90) {
                    s.roundState = RS.ROUND_END; s.rsFrame = 0;
                }
                if (s.roundState === RS.ROUND_END && s.rsFrame > 60) this.nextRound(s);
                return s;
            }

            if (s.roundState === RS.MATCH_END) { s.rsFrame++; for (let i = 0; i < 2; i++) this.physics(s, s.p[i], 0); return s; }

            // --- FIGHT ---
            s.timer--;
            if (s.timer <= 0) { s.timer = 0; this.timeOut(s); return s; }

            for (let i = 0; i < 2; i++) this.control(s, s.p[i], s.p[1 - i], masks[i] | 0);
            for (let i = 0; i < 2; i++) this.physics(s, s.p[i], 1);
            this.pushApart(s);
            for (let i = 0; i < 2; i++) this.resolveHits(s, s.p[i], s.p[1 - i]);
            for (let i = 0; i < 2; i++) this.facing(s, s.p[i], s.p[1 - i]);

            if (s.p[0].health <= 0 || s.p[1].health <= 0) this.knockout(s);
            return s;
        },

        facing(s, pl, op) {
            // Turn only while neutral — mid-attack turning is a classic exploit.
            if (pl.state === ST.IDLE || pl.state === ST.WALK_F || pl.state === ST.WALK_B || pl.state === ST.CROUCH) {
                pl.facing = op.x >= pl.x ? 1 : -1;
            }
        },

        control(s, pl, op, mask) {
            const d = this.def(pl);
            const prev = pl.lastMask; pl.lastMask = mask;
            const pressed = mask & ~prev;
            pl.sf++;

            if (pl.charge > 0) {
                pl.chargeTimer++;
                if (pl.chargeTimer >= CHARGE_DECAY) { pl.charge--; pl.chargeTimer = 0; }
            }
            if (pl.invuln > 0) pl.invuln--;

            // Passive traits — integer, keyed off sim state only, rollback-safe.
            // Kodama regrows while grounded and neutral: pressure denies it.
            if (d.regen && pl.health > 0 && pl.health < d.health && s.tick % 40 === 0 &&
                (pl.state === ST.IDLE || pl.state === ST.WALK_F || pl.state === ST.WALK_B || pl.state === ST.CROUCH)) {
                pl.health += d.regen;
                if (pl.health > d.health) pl.health = d.health;
            }
            // Ameonna's rain: one ki every meterRegen ticks, swing or not.
            if (d.meterRegen && pl.health > 0 && s.tick % d.meterRegen === 0) {
                pl.meter = Math.min(MAX_METER, pl.meter + 1);
            }

            const fwd = pl.facing;
            const holdF = (fwd > 0 ? (mask & IN.RIGHT) : (mask & IN.LEFT)) !== 0;
            const holdB = (fwd > 0 ? (mask & IN.LEFT) : (mask & IN.RIGHT)) !== 0;
            const holdD = (mask & IN.DOWN) !== 0;

            // Dash buffer: two forward taps inside 12 ticks.
            if (pl.dashBuf > 0) pl.dashBuf--;
            const tapF = (fwd > 0 ? (pressed & IN.RIGHT) : (pressed & IN.LEFT)) !== 0;
            const tapB = (fwd > 0 ? (pressed & IN.LEFT) : (pressed & IN.RIGHT)) !== 0;

            // ---- stun / recovery states first ----
            if (pl.state === ST.HITSTUN || pl.state === ST.HITSTUN_AIR) {
                pl.stun--;
                if (pl.state === ST.HITSTUN_AIR) { if (pl.y <= 0 && pl.vy <= 0) { pl.state = ST.KNOCKDOWN; pl.sf = 0; pl.stun = 34; pl.combo = 0; pl.comboDmg = 0; } }
                else if (pl.stun <= 0) { pl.state = ST.IDLE; pl.sf = 0; pl.combo = 0; pl.comboDmg = 0; }
                return;
            }
            if (pl.state === ST.KNOCKDOWN) {
                pl.stun--;
                if (pl.stun <= 0) { pl.state = ST.WAKEUP; pl.sf = 0; pl.invuln = 8; }
                return;
            }
            if (pl.state === ST.WAKEUP) { if (pl.sf > 12) { pl.state = ST.IDLE; pl.sf = 0; pl.combo = 0; pl.comboDmg = 0; } return; }

            if (pl.state === ST.LIGHT || pl.state === ST.HEAVY || pl.state === ST.SPECIAL) {
                const a = pl.state === ST.LIGHT ? d.attacks.light : pl.state === ST.HEAVY ? d.attacks.heavy : d.attacks.special;
                const total = a.startup + a.active + a.recovery;
                // One absorb per swing — armorUsed latches until the next attack.
                pl.armor = (a.armorFrom !== undefined && !pl.armorUsed && pl.sf >= a.armorFrom && pl.sf <= a.armorTo) ? 1 : 0;
                // Strike-invuln window (kitsune's feint). Refreshed each frame of
                // the window; the top-of-control decrement burns it right off.
                if (a.invulnFrom !== undefined && pl.sf >= a.invulnFrom && pl.sf <= a.invulnTo) pl.invuln = 2;

                // Cancel window: a connected attack can be cancelled into the
                // next link during recovery. This is where combos come from.
                if (pl.canCancel && pl.sf >= a.startup + a.active) {
                    if (a.cancel === "heavy" && (pressed & IN.HEAVY)) { this.startAttack(s, pl, ST.HEAVY); return; }
                    if (a.cancel === "special" && (pressed & IN.SPECIAL) && pl.meter >= (d.attacks.special.cost || 0)) { this.startAttack(s, pl, ST.SPECIAL); return; }
                }
                if (pl.sf >= total) { pl.state = pl.airborne ? ST.JUMP : ST.IDLE; pl.sf = 0; pl.armor = 0; pl.canCancel = 0; }
                return;
            }

            if (pl.state === ST.DASH) {
                pl.vx = fwd * (d.dashSpd + pl.charge * 60);
                if (pl.sf >= d.dashDur) { pl.state = ST.IDLE; pl.sf = 0; pl.vx = 0; }
                else if (pressed & (IN.LIGHT | IN.HEAVY)) { this.startAttack(s, pl, (pressed & IN.HEAVY) ? ST.HEAVY : ST.LIGHT); }
                return;
            }
            if (pl.state === ST.BACKDASH) {
                if (pl.sf < 6) pl.invuln = 2;
                pl.vx = -fwd * idiv(d.dashSpd * 7, 10);
                if (pl.sf >= 18) { pl.state = ST.IDLE; pl.sf = 0; pl.vx = 0; }
                return;
            }

            // ---- airborne ----
            if (pl.airborne) {
                if (pressed & IN.HEAVY) { this.startAttack(s, pl, ST.HEAVY); return; }
                if (pressed & IN.LIGHT) { this.startAttack(s, pl, ST.LIGHT); return; }
                if (holdF) pl.vx += d.airDrift; else if (holdB) pl.vx -= d.airDrift;
                const cap = d.walkF + 120;
                if (pl.vx > cap) pl.vx = cap; if (pl.vx < -cap) pl.vx = -cap;
                pl.state = ST.JUMP;
                return;
            }

            // ---- grounded neutral ----
            if (pressed & IN.SPECIAL) {
                const sp = d.attacks.special;
                if (pl.meter >= (sp.cost || 0)) { this.startAttack(s, pl, ST.SPECIAL); return; }
            }
            if (pressed & IN.HEAVY) { this.startAttack(s, pl, ST.HEAVY); return; }
            if (pressed & IN.LIGHT) { this.startAttack(s, pl, ST.LIGHT); return; }

            if (tapF) { if (pl.dashBuf > 0 && pl.dashDir === 1) { pl.state = ST.DASH; pl.sf = 0; pl.dashBuf = 0; return; } pl.dashBuf = 12; pl.dashDir = 1; }
            else if (tapB) { if (pl.dashBuf > 0 && pl.dashDir === -1) { pl.state = ST.BACKDASH; pl.sf = 0; pl.dashBuf = 0; return; } pl.dashBuf = 12; pl.dashDir = -1; }

            if (mask & IN.UP) {
                pl.vy = d.jumpV; pl.airborne = 1; pl.state = ST.JUMP; pl.sf = 0;
                pl.vx = holdF ? fwd * d.walkF : holdB ? -fwd * d.walkB : 0;
                return;
            }

            // Blocking is holding away — no dedicated block button, like the
            // games this is descended from.
            const chargeSpd = idiv(pl.charge * 6, 100);
            if (holdB) {
                pl.state = holdD ? ST.BLOCK_LOW : ST.BLOCK;
                pl.vx = -fwd * idiv(d.walkB * (100 + chargeSpd * 100), 100);
                pl.sf = 0;
                return;
            }
            if (holdD) { pl.state = ST.CROUCH; pl.vx = 0; return; }
            if (holdF) { pl.state = ST.WALK_F; pl.vx = fwd * (d.walkF + pl.charge * 22); return; }

            pl.state = ST.IDLE; pl.vx = 0;
        },

        startAttack(s, pl, st) {
            const d = this.def(pl);
            const a = st === ST.LIGHT ? d.attacks.light : st === ST.HEAVY ? d.attacks.heavy : d.attacks.special;
            if (st === ST.SPECIAL) {
                pl.meter -= (a.cost || 0);
                if (pl.meter < 0) pl.meter = 0;
                // Hitodama burns her own flame to light yours — but the cost can
                // never be the killing blow; only the opponent gets that.
                if (a.selfCost) { pl.health -= a.selfCost; if (pl.health < 1) pl.health = 1; }
                if (a.rush) pl.vx = pl.facing * a.rush;
                this.spawnFx(s, 2, pl.x, pl.y + px(70), pl.ci, pl.facing);
            }
            pl.state = st; pl.sf = 0; pl.hitUsed = 0; pl.canCancel = 0; pl.armor = 0; pl.armorUsed = 0;
            if (!pl.airborne && st !== ST.SPECIAL) pl.vx = 0;
        },

        physics(s, pl, live) {
            const d = this.def(pl);
            pl.x += pl.vx;
            if (pl.airborne || pl.y > 0) {
                pl.y += pl.vy;
                pl.vy -= d.gravity;
                if (pl.y <= 0) {
                    pl.y = 0; pl.vy = 0;
                    if (pl.airborne) {
                        pl.airborne = 0; pl.juggle = 0;
                        if (pl.state === ST.JUMP || pl.state === ST.LIGHT || pl.state === ST.HEAVY) { pl.state = ST.IDLE; pl.sf = 0; }
                    }
                }
            } else {
                // ground friction (knockback slide)
                if (pl.state === ST.HITSTUN || pl.state === ST.KNOCKDOWN || pl.state === ST.WAKEUP) {
                    if (pl.vx > 0) { pl.vx -= 60; if (pl.vx < 0) pl.vx = 0; }
                    else if (pl.vx < 0) { pl.vx += 60; if (pl.vx > 0) pl.vx = 0; }
                }
            }
            if (pl.x < -STAGE_HALF) { pl.x = -STAGE_HALF; if (pl.vx < 0) pl.vx = 0; }
            if (pl.x > STAGE_HALF) { pl.x = STAGE_HALF; if (pl.vx > 0) pl.vx = 0; }
        },

        pushApart(s) {
            const a = s.p[0], b = s.p[1];
            if (a.airborne && b.airborne) return;
            const dx = b.x - a.x;
            const dist = dx < 0 ? -dx : dx;
            if (dist >= PUSH_RADIUS * 2) return;
            const over = PUSH_RADIUS * 2 - dist;
            const half = idiv(over, 2);
            const dir = dx >= 0 ? 1 : -1;
            a.x -= dir * half; b.x += dir * half;
            if (a.x < -STAGE_HALF) { a.x = -STAGE_HALF; b.x = a.x + dir * PUSH_RADIUS * 2; }
            if (b.x > STAGE_HALF) { b.x = STAGE_HALF; a.x = b.x - dir * PUSH_RADIUS * 2; }
            if (a.x > STAGE_HALF) a.x = STAGE_HALF;
            if (b.x < -STAGE_HALF) b.x = -STAGE_HALF;
        },

        hurtbox(pl) {
            const low = (pl.state === ST.CROUCH || pl.state === ST.BLOCK_LOW || pl.state === ST.KNOCKDOWN);
            const h = pl.state === ST.KNOCKDOWN ? px(50) : low ? CROUCH_H : HURT_H;
            return { x: pl.x - idiv(HURT_W, 2), y: pl.y, w: HURT_W, h: h };
        },

        hitbox(pl) {
            const d = this.def(pl);
            let a = null;
            if (pl.state === ST.LIGHT) a = d.attacks.light;
            else if (pl.state === ST.HEAVY) a = d.attacks.heavy;
            else if (pl.state === ST.SPECIAL) a = d.attacks.special;
            if (!a) return null;
            if (pl.sf < a.startup || pl.sf >= a.startup + a.active) return null;
            const b = a.box;      // [xOffset, yOffset, w, h] in facing-space px
            const x = pl.facing > 0 ? pl.x + px(b[0]) : pl.x - px(b[0]) - px(b[2]);
            return { x: x, y: pl.y + px(b[1]), w: px(b[2]), h: px(b[3]), a: a };
        },

        overlap(r1, r2) {
            return r1.x < r2.x + r2.w && r1.x + r1.w > r2.x && r1.y < r2.y + r2.h && r1.y + r1.h > r2.y;
        },

        resolveHits(s, att, def) {
            if (att.hitUsed) return;
            const hb = this.hitbox(att);
            if (!hb) return;
            const hurt = this.hurtbox(def);
            if (!this.overlap(hb, hurt)) return;

            att.hitUsed = 1;
            const a = hb.a;
            const ad = this.def(att);

            if (def.invuln > 0) return;

            // --- guard check ---
            const guarding = (def.state === ST.BLOCK || def.state === ST.BLOCK_LOW) && !def.airborne;
            let blocked = false;
            if (guarding) {
                const crouchGuard = def.state === ST.BLOCK_LOW;
                if (a.guard === GUARD.MID) blocked = true;
                else if (a.guard === GUARD.LOW) blocked = crouchGuard;
                else if (a.guard === GUARD.HIGH) blocked = !crouchGuard;
                // Oni's grip beats guard at point blank — the grappler's whole
                // reason to exist. Range check keeps it honest.
                if (a.unblockableClose) {
                    const gap = (def.x - att.x) * (att.facing > 0 ? 1 : -1);
                    if (gap < px(a.unblockableClose)) blocked = false;
                }
            }

            if (blocked) {
                const chip = idiv(a.dmg, 12);
                def.health -= chip;
                def.stun = a.blockstun;
                def.state = def.state === ST.BLOCK_LOW ? ST.BLOCK_LOW : ST.BLOCK;
                def.sf = 0;
                def.vx = att.facing * idiv(a.kb, 3);
                att.vx = -att.facing * idiv(a.kb, 6);
                att.meter = Math.min(MAX_METER, att.meter + idiv(a.meter, 2));
                // Bakeneko profits from watching the swing land on her guard.
                def.meter = Math.min(MAX_METER, def.meter + idiv(a.meter, 2) + (this.def(def).blockMeter || 0));
                s.hitstop = idiv(a.hitstop, 2);
                s.shake = 3;
                att.canCancel = 1;
                // Raiju bleeds charge when the pressure is read.
                if (ad.key === "raiju" && att.charge > 0) att.charge--;
                this.spawnFx(s, 1, def.x + att.facing * px(20), def.y + px(88), def.ci, att.facing);
                return;
            }

            // --- armor absorb ---
            if (def.armor && !def.airborne && a.dmg < 150) {
                // Armour buys you the stagger, not the damage.
                def.health -= idiv(a.dmg * (this.def(def).armorCost || 100), 100);
                def.armor = 0; def.armorUsed = 1;
                att.meter = Math.min(MAX_METER, att.meter + a.meter);
                s.hitstop = 6; s.shake = 4;
                this.spawnFx(s, 3, def.x, def.y + px(90), def.ci, att.facing);
                return;
            }

            // --- clean hit ---
            def.combo++;
            const scale = Math.max(30, 100 - (def.combo - 1) * 12);   // damage scaling
            // CHARGE pays out in damage as well as speed — the whole reason to
            // take Raiju's risk. Blocked and whiffed swings burn it back off.
            const chargeBonus = 100 + att.charge * 5;
            const dmg = Math.max(6, idiv(idiv(a.dmg * scale, 100) * chargeBonus, 100));
            def.health -= dmg;
            if (def.health < 0) def.health = 0;
            def.comboDmg += dmg;

            // Hitodama's siphon: a cut of dealt damage flows back as health.
            if (a.leech) {
                att.health += idiv(dmg * a.leech, 100);
                const cap = ad.health;
                if (att.health > cap) att.health = cap;
            }

            const kbScale = idiv(100 * 88, this.def(def).weight);
            def.vx = att.facing * idiv(a.kb * kbScale, 100);
            // Only a flagged launcher lifts a grounded opponent. Without this
            // every heavy ends in a knockdown and neutral never restarts.
            if ((a.kbUp > 0 && (a.launch || def.airborne)) || def.airborne) {
                def.vy = a.kbUp > 0 ? idiv(a.kbUp * kbScale, 100) : 200;
                def.airborne = 1;
                def.juggle++;
                def.state = ST.HITSTUN_AIR;
            } else {
                def.state = ST.HITSTUN;
            }
            def.stun = a.hitstun;
            def.sf = 0;
            def.hitUsed = 0;
            def.canCancel = 0;

            att.meter = Math.min(MAX_METER, att.meter + a.meter);
            def.meter = Math.min(MAX_METER, def.meter + idiv(a.meter, 3));
            att.canCancel = 1;
            s.hitstop = a.hitstop;
            s.shake = 4 + idiv(a.hitstop, 2);

            if (ad.key === "raiju" && att.charge < MAX_CHARGE) { att.charge++; att.chargeTimer = 0; }

            this.spawnFx(s, 0, def.x + att.facing * px(14), def.y + px(hb.y > def.y + px(70) ? 100 : 70), att.ci, att.facing);
        },

        knockout(s) {
            s.roundState = RS.KO; s.rsFrame = 0; s.hitstop = 16; s.shake = 14;
            const dead = s.p[0].health <= 0 ? 0 : 1;
            const alive = 1 - dead;
            if (s.p[0].health <= 0 && s.p[1].health <= 0) { s.p[0].state = ST.LOSE; s.p[1].state = ST.LOSE; s.winner = 0; }
            else {
                s.p[dead].state = ST.LOSE; s.p[dead].sf = 0; s.p[dead].vy = 300; s.p[dead].vx = -s.p[dead].facing * 400; s.p[dead].airborne = 1;
                s.p[alive].wins++;
                s.winner = alive + 1;
            }
        },

        timeOut(s) {
            s.roundState = RS.KO; s.rsFrame = 0;
            if (s.p[0].health === s.p[1].health) s.winner = 0;
            else { const w = s.p[0].health > s.p[1].health ? 0 : 1; s.p[w].wins++; s.winner = w + 1; s.p[1 - w].state = ST.LOSE; }
        },

        nextRound(s) {
            if (s.p[0].wins >= ROUNDS_TO_WIN || s.p[1].wins >= ROUNDS_TO_WIN) {
                s.roundState = RS.MATCH_END; s.rsFrame = 0;
                const w = s.p[0].wins > s.p[1].wins ? 0 : 1;
                s.p[w].state = ST.WIN; s.p[w].sf = 0;
                s.p[1 - w].state = ST.LOSE;
                s.winner = w + 1;
                return;
            }
            s.round++;
            s.timer = ROUND_TICKS;
            s.roundState = RS.INTRO; s.rsFrame = 0;
            s.fx.length = 0;
            for (let i = 0; i < 2; i++) {
                const pl = s.p[i], d = ROSTER[pl.ci];
                pl.x = i === 0 ? -px(190) : px(190);
                pl.y = 0; pl.vx = 0; pl.vy = 0;
                pl.facing = i === 0 ? 1 : -1;
                pl.state = ST.INTRO; pl.sf = 0;
                pl.health = d.health;
                pl.stun = 0; pl.combo = 0; pl.comboDmg = 0;
                pl.hitUsed = 0; pl.canCancel = 0; pl.invuln = 0; pl.armor = 0; pl.armorUsed = 0;
                pl.airborne = 0; pl.juggle = 0; pl.charge = 0; pl.chargeTimer = 0;
                pl.dashBuf = 0; pl.lastMask = 0;
                pl.meter = Math.min(MAX_METER, idiv(pl.meter, 2));
            }
        }
    };

    // ==================================================================
    // AI — reads sim state, emits an input mask. Deterministic: the only
    // randomness comes from Sim.rand, which lives in the sim state, so an
    // AI match replays identically from its seed.
    // ==================================================================

    const AI = {
        // level: 0 casual, 1 ward, 2 bloodline
        think(s, meIdx, level) {
            const me = s.p[meIdx], op = s.p[1 - meIdx];
            if (s.roundState !== RS.FIGHT) return 0;
            const d = Sim.def(me), od = Sim.def(op);

            const gap = Math.abs(op.x - me.x);
            const toward = op.x > me.x ? IN.RIGHT : IN.LEFT;
            const away = op.x > me.x ? IN.LEFT : IN.RIGHT;
            const myHeavyReach = px(d.attacks.heavy.box[0] + d.attacks.heavy.box[2]);

            // Closing the gap. This runs OUTSIDE the reaction gate because it is
            // navigation, not reading the opponent — and because a dash needs a
            // clean press-release-press that a stale held direction would eat.
            // Without it the slower character can never start its offence.
            const idleish = (me.state === ST.IDLE || me.state === ST.WALK_F || me.state === ST.WALK_B || me.state === ST.CROUCH);
            if (idleish && gap > myHeavyReach + px(70)) {
                const phase = (s.tick + meIdx * 3) % 6;
                return phase < 2 ? toward : 0;      // tap, release, tap → dash
            }
            if (me.state === ST.DASH) {
                // ride the dash; swing as soon as the target is inside range
                return gap < myHeavyReach ? (toward | IN.HEAVY) : 0;
            }

            // Reaction gating — weaker AI acts on stale information.
            const react = level === 0 ? 12 : level === 1 ? 7 : 3;
            if (s.tick % react !== meIdx % react) {
                // hold whatever directional it had, but never hold buttons
                return me.lastMask & (IN.LEFT | IN.RIGHT | IN.DOWN);
            }
            const lightRange = px(d.attacks.light.box[0] + d.attacks.light.box[2]);
            const heavyRange = px(d.attacks.heavy.box[0] + d.attacks.heavy.box[2]);
            const specRange = px(d.attacks.special.box[0] + d.attacks.special.box[2]);

            if (me.state === ST.HITSTUN || me.state === ST.HITSTUN_AIR || me.state === ST.KNOCKDOWN) return 0;

            // Cash out a connected hit. Light → heavy → special is the bread-and-
            // butter route; without this the AI trades pokes and never threatens.
            if (me.canCancel && (me.state === ST.LIGHT || me.state === ST.HEAVY)) {
                const comboIQ = level === 0 ? 30 : level === 1 ? 70 : 95;
                if (Sim.rand(s, 100) < comboIQ) {
                    if (me.state === ST.HEAVY && me.meter >= (d.attacks.special.cost || 0)) return IN.SPECIAL;
                    if (me.state === ST.LIGHT) return IN.HEAVY;
                }
            }

            const opAttacking = (op.state === ST.LIGHT || op.state === ST.HEAVY || op.state === ST.SPECIAL);
            const opStartup = opAttacking && op.sf < (Sim.def(op).attacks[op.state === ST.LIGHT ? "light" : op.state === ST.HEAVY ? "heavy" : "special"].startup + 2);

            // Respect the command grab. Blocking loses to an unblockable, so
            // once the grappler has meter and is in grab range the answer is to
            // leave — the counterplay a human learns in about three rounds.
            const grab = od.attacks.special.unblockableClose;
            if (grab && op.meter >= (od.attacks.special.cost || 0)) {
                const danger = px(grab) + px(34);
                if (gap < danger && !opAttacking) {
                    const iq = level === 0 ? 20 : level === 1 ? 55 : 88;
                    if (Sim.rand(s, 100) < iq) {
                        // jump out or backdash out; occasionally beat it to the punch
                        if (Sim.rand(s, 100) < 30) return toward | IN.LIGHT;
                        return away | (Sim.rand(s, 100) < 45 ? IN.UP : 0);
                    }
                }
            }

            // A grab in startup cannot be blocked — guarding here is the losing
            // option. Jump or run; the startup is long enough to react to.
            if (grab && op.state === ST.SPECIAL && op.sf < od.attacks.special.startup && gap < px(grab) + px(40)) {
                const iq = level === 0 ? 25 : level === 1 ? 60 : 92;
                if (Sim.rand(s, 100) < iq) return away | IN.UP;
            }

            // A deep-stunned opponent (yukionna's freeze, special launchers) is a
            // free hit — walk in and take it instead of idling politely.
            if ((op.state === ST.HITSTUN || op.state === ST.HITSTUN_AIR) && op.stun > 26 && gap < heavyRange) {
                return toward | IN.HEAVY;
            }

            // Block incoming pressure — higher levels block more reliably, and
            // read the guard level of what's coming instead of guessing height.
            if (opAttacking && gap < heavyRange + px(30)) {
                const blockOdds = level === 0 ? 35 : level === 1 ? 62 : 84;
                if (Sim.rand(s, 100) < blockOdds) {
                    const oa = Sim.def(op).attacks[op.state === ST.LIGHT ? "light" : op.state === ST.HEAVY ? "heavy" : "special"];
                    const readOdds = level === 0 ? 0 : level === 1 ? 45 : 75;
                    let down = Sim.rand(s, 100) < 30;
                    if (Sim.rand(s, 100) < readOdds) down = oa.guard === GUARD.LOW;
                    return away | (down ? IN.DOWN : 0);
                }
            }

            // Punish a whiffed/recovering opponent.
            const opRecovering = opAttacking && !opStartup && op.hitUsed === 0;
            if (opRecovering && gap < heavyRange) {
                if (me.meter >= (d.attacks.special.cost || 0) && gap < specRange) return toward | IN.SPECIAL;
                return toward | IN.HEAVY;
            }

            // In range: mix up.
            if (gap < lightRange) {
                const r = Sim.rand(s, 100);
                const aggro = level === 0 ? 55 : level === 1 ? 72 : 84;
                if (r < aggro) {
                    if (me.meter >= 50 && r < 12) return toward | IN.SPECIAL;
                    if (r < aggro - 30) return IN.LIGHT;
                    return IN.HEAVY;
                }
                if (r < aggro + 12) return away;           // reset spacing
                return 0;
            }

            if (gap < heavyRange + px(20)) {
                const r = Sim.rand(s, 100);
                if (r < (level === 2 ? 55 : 38)) return toward | IN.HEAVY;
                return toward;
            }

            // Approach. Fast bloodlines dash to build pressure; tanks lumber.
            const r = Sim.rand(s, 100);
            if (d.walkF >= 330 && r < 18) return toward;   // repeated taps → dash
            if (r < 8 && gap > px(260)) return toward | IN.UP;
            return toward;
        }
    };

    // ==================================================================
    // RENDERER — ink-shadow fighters. Reads sim state, never writes it.
    // Skeleton is forward-kinematic: poses are joint angles, so a new
    // character is a data change, not new drawing code.
    // ==================================================================

    const LIMB = { upperArm: 26, foreArm: 25, thigh: 31, shin: 31, torso: 42, neck: 12, headR: 17 };

    const D2R = Math.PI / 180;

    // pose = { hipY, lean, torsoA, headA, armF:[a,b], armB:[a,b], legF:[a,b], legB:[a,b] }
    function pose(hipY, lean, torsoA, headA, armF, armB, legF, legB) {
        return { hipY: hipY, lean: lean, torsoA: torsoA, headA: headA, armF: armF, armB: armB, legF: legF, legB: legB };
    }

    const POSE = {
        idle: pose(64, 0, -4, 2, [24, 42], [16, 50], [12, 16], [-14, 20]),
        idle2: pose(66, 0, -6, 3, [20, 46], [12, 54], [10, 14], [-12, 18]),
        walkF: pose(62, 3, -6, 3, [30, 40], [10, 46], [26, 10], [-26, 34]),
        walkF2: pose(62, 3, -6, 3, [22, 46], [22, 40], [-24, 32], [24, 12]),
        walkB: pose(63, -3, -2, 0, [26, 44], [14, 48], [-20, 28], [20, 12]),
        crouch: pose(38, 2, -14, 6, [34, 62], [26, 66], [46, 78], [-40, 74]),
        jump: pose(70, 2, -10, 4, [46, 30], [-30, 40], [40, 70], [-16, 54]),
        block: pose(60, -6, -10, 4, [58, 96], [50, 92], [14, 20], [-18, 24]),
        blockLow: pose(37, -4, -18, 6, [60, 92], [52, 88], [48, 80], [-42, 76]),
        hit: pose(60, -10, -20, -14, [-26, 34], [-38, 30], [-8, 22], [16, 26]),
        hitAir: pose(66, -12, -26, -18, [-40, 26], [-52, 22], [-24, 34], [10, 40]),
        down: pose(15, -6, -82, 8, [-102, 18], [-112, 14], [86, -16], [78, -10]),
        wake: pose(44, -4, -40, 0, [-20, 60], [-32, 62], [52, 44], [-16, 40]),
        dash: pose(58, 8, -16, 6, [44, 26], [-24, 36], [34, 26], [-30, 40]),
        backdash: pose(62, -8, -6, 2, [20, 50], [8, 56], [-34, 44], [22, 20]),
        win: pose(66, 0, 2, -4, [-56, 22], [-48, 28], [8, 12], [-16, 18]),
        lose: pose(18, -8, -78, 4, [-96, 22], [-108, 16], [82, -14], [72, -8]),
        intro: pose(63, 0, -3, 1, [22, 44], [14, 52], [10, 14], [-12, 18]),

        // attacks — [windup, strike, recover]
        lightW: pose(62, -2, -8, 2, [-8, 74], [22, 50], [10, 14], [-14, 20]),
        lightS: pose(62, 6, 4, 0, [86, 6], [-14, 44], [16, 12], [-20, 26]),
        lightR: pose(62, 2, -2, 2, [52, 40], [6, 50], [12, 14], [-16, 22]),

        heavyW: pose(60, -8, -18, -4, [-52, 92], [-34, 84], [-16, 26], [18, 18]),
        heavyS: pose(58, 12, 12, 6, [104, 10], [30, 30], [34, 16], [-30, 34]),
        heavyR: pose(58, 4, 2, 4, [70, 46], [18, 52], [20, 20], [-22, 28]),

        specW: pose(56, -10, -24, -8, [-64, 104], [-50, 96], [-20, 34], [22, 22]),
        specS: pose(60, 14, 16, 8, [112, 4], [78, 20], [40, 14], [-34, 38]),
        specR: pose(58, 2, -4, 2, [60, 54], [40, 58], [18, 22], [-20, 26])
    };

    function lerpPose(a, b, t) {
        const L = (x, y) => x + (y - x) * t;
        return {
            hipY: L(a.hipY, b.hipY), lean: L(a.lean, b.lean),
            torsoA: L(a.torsoA, b.torsoA), headA: L(a.headA, b.headA),
            armF: [L(a.armF[0], b.armF[0]), L(a.armF[1], b.armF[1])],
            armB: [L(a.armB[0], b.armB[0]), L(a.armB[1], b.armB[1])],
            legF: [L(a.legF[0], b.legF[0]), L(a.legF[1], b.legF[1])],
            legB: [L(a.legB[0], b.legB[0]), L(a.legB[1], b.legB[1])]
        };
    }

    function poseFor(pl, def) {
        const f = pl.sf;
        switch (pl.state) {
            case ST.IDLE: {
                const t = (Math.sin(f * 0.09) + 1) / 2;
                return lerpPose(POSE.idle, POSE.idle2, t);
            }
            case ST.INTRO: return POSE.intro;
            case ST.WALK_F: { const t = (Math.sin(f * 0.24) + 1) / 2; return lerpPose(POSE.walkF, POSE.walkF2, t); }
            case ST.WALK_B: { const t = (Math.sin(f * 0.2) + 1) / 2; return lerpPose(POSE.walkB, POSE.idle, t * 0.6); }
            case ST.CROUCH: return POSE.crouch;
            case ST.JUMP: return POSE.jump;
            case ST.BLOCK: return POSE.block;
            case ST.BLOCK_LOW: return POSE.blockLow;
            case ST.HITSTUN: return POSE.hit;
            case ST.HITSTUN_AIR: return POSE.hitAir;
            case ST.KNOCKDOWN: return POSE.down;
            case ST.WAKEUP: return lerpPose(POSE.down, POSE.wake, Math.min(1, f / 12));
            case ST.DASH: return POSE.dash;
            case ST.BACKDASH: return POSE.backdash;
            case ST.WIN: return POSE.win;
            case ST.LOSE: return POSE.lose;
            case ST.LIGHT: case ST.HEAVY: case ST.SPECIAL: {
                const a = pl.state === ST.LIGHT ? def.attacks.light : pl.state === ST.HEAVY ? def.attacks.heavy : def.attacks.special;
                const W = pl.state === ST.LIGHT ? POSE.lightW : pl.state === ST.HEAVY ? POSE.heavyW : POSE.specW;
                const K = pl.state === ST.LIGHT ? POSE.lightS : pl.state === ST.HEAVY ? POSE.heavyS : POSE.specS;
                const R = pl.state === ST.LIGHT ? POSE.lightR : pl.state === ST.HEAVY ? POSE.heavyR : POSE.specR;
                if (f < a.startup) return lerpPose(POSE.idle, W, Math.min(1, f / Math.max(1, a.startup - 1)));
                if (f < a.startup + a.active) return lerpPose(W, K, Math.min(1, (f - a.startup) / Math.max(1, a.active - 1) + 0.5));
                const rt = (f - a.startup - a.active) / Math.max(1, a.recovery);
                return rt < 0.4 ? lerpPose(K, R, rt / 0.4) : lerpPose(R, POSE.idle, (rt - 0.4) / 0.6);
            }
        }
        return POSE.idle;
    }

    function joints(p, facing) {
        // Local space: origin at feet, +y up, +x toward the opponent.
        const fx = facing;
        const A = (deg) => deg * D2R;
        const hip = { x: p.lean * fx, y: p.hipY };
        const ta = A(p.torsoA);
        const chest = { x: hip.x + Math.sin(ta) * LIMB.torso * fx, y: hip.y + Math.cos(ta) * LIMB.torso };
        const na = A(p.torsoA + p.headA);
        const head = { x: chest.x + Math.sin(na) * (LIMB.neck + LIMB.headR) * fx, y: chest.y + Math.cos(na) * (LIMB.neck + LIMB.headR) };

        const arm = (angles, origin) => {
            const a1 = A(angles[0]), a2 = A(angles[0] + angles[1]);
            const elbow = { x: origin.x + Math.sin(a1) * LIMB.upperArm * fx, y: origin.y - Math.cos(a1) * LIMB.upperArm };
            const hand = { x: elbow.x + Math.sin(a2) * LIMB.foreArm * fx, y: elbow.y - Math.cos(a2) * LIMB.foreArm };
            return [origin, elbow, hand];
        };
        const leg = (angles) => {
            const a1 = A(angles[0]), a2 = A(angles[0] + angles[1]);
            const knee = { x: hip.x + Math.sin(a1) * LIMB.thigh * fx, y: hip.y - Math.cos(a1) * LIMB.thigh };
            const foot = { x: knee.x + Math.sin(a2) * LIMB.shin * fx, y: knee.y - Math.cos(a2) * LIMB.shin };
            return [hip, knee, foot];
        };
        const shF = { x: chest.x + 3 * fx, y: chest.y - 4 };
        const shB = { x: chest.x - 5 * fx, y: chest.y - 5 };
        return {
            hip: hip, chest: chest, head: head,
            armF: arm(p.armF, shF), armB: arm(p.armB, shB),
            legF: leg(p.legF), legB: leg(p.legB)
        };
    }

    // ==================================================================
    // GAME SHELL — canvas, loop, input, UI
    // ==================================================================

    const WORLD_SCALE = 1.5;    // fighters are ~142 world-px tall; the ring is 540 tall
    const cv = document.getElementById("arena-canvas");
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const VW = 1280, VH = 540;
    const GROUND_PY = 452;              // canvas y of the ground line

    let dpr = 1;
    function resize() {
        const rect = cv.parentElement.getBoundingClientRect();
        const w = Math.max(320, Math.min(rect.width, 1280));
        const h = Math.round(w * VH / VW);
        dpr = Math.min(2, window.devicePixelRatio || 1);
        cv.style.width = w + "px";
        cv.style.height = h + "px";
        cv.width = Math.round(w * dpr);
        cv.height = Math.round(h * dpr);
    }
    window.addEventListener("resize", resize);

    // ---- input ------------------------------------------------------

    const KEYMAP_P1 = { KeyA: IN.LEFT, KeyD: IN.RIGHT, KeyW: IN.UP, KeyS: IN.DOWN, KeyJ: IN.LIGHT, KeyK: IN.HEAVY, KeyL: IN.SPECIAL };
    const KEYMAP_P2 = { ArrowLeft: IN.LEFT, ArrowRight: IN.RIGHT, ArrowUp: IN.UP, ArrowDown: IN.DOWN, Numpad1: IN.LIGHT, Numpad2: IN.HEAVY, Numpad3: IN.SPECIAL, Comma: IN.LIGHT, Period: IN.HEAVY, Slash: IN.SPECIAL };

    let mask1 = 0, mask2 = 0, touchMask = 0;
    let capture = false;

    function onKey(e, down) {
        if (!capture) return;
        const b1 = KEYMAP_P1[e.code], b2 = KEYMAP_P2[e.code];
        if (b1 === undefined && b2 === undefined) return;
        e.preventDefault();
        if (b1 !== undefined) mask1 = down ? (mask1 | b1) : (mask1 & ~b1);
        if (b2 !== undefined) mask2 = down ? (mask2 | b2) : (mask2 & ~b2);
    }
    window.addEventListener("keydown", (e) => onKey(e, true));
    window.addEventListener("keyup", (e) => onKey(e, false));
    window.addEventListener("blur", () => { mask1 = 0; mask2 = 0; });

    // Touch pad (mobile). Buttons are DOM so they get real hit targets.
    const pad = document.getElementById("arena-pad");
    if (pad) {
        pad.querySelectorAll("[data-btn]").forEach((btn) => {
            const bit = IN[btn.getAttribute("data-btn")];
            const on = (e) => { e.preventDefault(); touchMask |= bit; btn.classList.add("held"); };
            const off = (e) => { e.preventDefault(); touchMask &= ~bit; btn.classList.remove("held"); };
            btn.addEventListener("touchstart", on, { passive: false });
            btn.addEventListener("touchend", off, { passive: false });
            btn.addEventListener("touchcancel", off, { passive: false });
            btn.addEventListener("mousedown", on);
            btn.addEventListener("mouseup", off);
            btn.addEventListener("mouseleave", off);
        });
    }

    // ---- match session ----------------------------------------------

    let sim = null;
    let mode = "cpu";            // "cpu" | "local"
    let aiLevel = 1;
    let running = false;
    let acc = 0, last = 0;
    const TICK_MS = 1000 / 60;
    let trails = [[], []];
    // Render-only splash ring buffer: water kicked up by feet. Cosmetic, so it
    // lives outside the sim (like `trails`) and never affects rollback.
    let splashes = [];
    let wasAir = [false, false];
    function spawnSplash(x, power) { if (splashes.length < 40) splashes.push({ x: x, life: 0, power: power }); }
    let announce = { text: "", sub: "", life: 0 };
    let matchResult = null;

    function startMatch(charA, charB, m, lvl) {
        mode = m; aiLevel = lvl;
        const seed = (Date.now() ^ (Math.random() * 0xffffffff)) | 0;   // seed only — sim itself stays pure
        sim = Sim.create(seed, charA, charB);
        trails = [[], []]; splashes = []; wasAir = [false, false];
        matchResult = null;
        announce = { text: "ROUND 1", sub: "闘 START", life: 0 };
        running = true; capture = true; acc = 0; last = 0;
        root.classList.add("arena-live");
        setScreen("fight");
        requestAnimationFrame(loop);
    }

    function endMatch() {
        running = false; capture = false;
        root.classList.remove("arena-live");
    }

    function loop(ts) {
        if (!running) return;
        if (!last) last = ts;
        let dt = ts - last; last = ts;
        if (dt > 250) dt = 250;                  // tab-switch guard
        acc += dt;
        let steps = 0;
        while (acc >= TICK_MS && steps < 5) {
            step();
            acc -= TICK_MS; steps++;
        }
        render();
        requestAnimationFrame(loop);
    }

    let prevRound = 1, prevRS = RS.INTRO;

    function step() {
        if (Net.active) {
            if (!Net.step()) return;        // stalled waiting for the peer
        } else {
            const m1 = mask1 | touchMask;
            const m2 = mode === "local" ? mask2 : AI.think(sim, 1, aiLevel);
            Sim.tick(sim, [m1, m2]);
        }

        // render-side ghost trail buffer
        for (let i = 0; i < 2; i++) {
            const pl = sim.p[i];
            const fast = Math.abs(pl.vx) > 380 || pl.state === ST.DASH || pl.state === ST.SPECIAL;
            if (fast && sim.tick % 2 === 0) {
                trails[i].push({ x: pl.x, y: pl.y, facing: pl.facing, p: poseFor(pl, Sim.def(pl)), life: 0 });
                if (trails[i].length > 6) trails[i].shift();
            }
            for (let k = trails[i].length - 1; k >= 0; k--) { trails[i][k].life++; if (trails[i][k].life > 14) trails[i].splice(k, 1); }

            // water kicked up by the feet: a burst on landing, a patter while running
            const air = pl.y > 0;
            if (wasAir[i] && !air) spawnSplash(pl.x, 1);
            else if (!air && Math.abs(pl.vx) > 200 && sim.tick % 9 === 0) spawnSplash(pl.x, 0.42);
            wasAir[i] = air;
        }
        for (let k = splashes.length - 1; k >= 0; k--) { if (++splashes[k].life > 22) splashes.splice(k, 1); }

        if (sim.round !== prevRound) { announce = { text: "ROUND " + sim.round, sub: "闘 START", life: 0 }; prevRound = sim.round; }
        if (sim.roundState !== prevRS) {
            if (sim.roundState === RS.KO) {
                const perfect = sim.winner > 0 && sim.p[sim.winner - 1].health === Sim.def(sim.p[sim.winner - 1]).health;
                announce = { text: sim.timer <= 0 ? "TIME" : "K.O.", sub: perfect ? "完璧 PERFECT" : "", life: 0 };
            }
            if (sim.roundState === RS.MATCH_END) {
                const w = sim.winner - 1;
                announce = { text: ROSTER[sim.p[w].ci].name + " WINS", sub: ROSTER[sim.p[w].ci].kanji, life: 0 };
                matchResult = { winner: w, rounds: [sim.p[0].wins, sim.p[1].wins] };
                setTimeout(() => { if (sim && sim.roundState === RS.MATCH_END) showResult(); }, 2200);
            }
            prevRS = sim.roundState;
        }
        announce.life++;
    }

    // ---- drawing ----------------------------------------------------

    function hexRGBA(hex, a) {
        const n = parseInt(hex.slice(1), 16);
        return "rgba(" + ((n >> 16) & 255) + "," + ((n >> 8) & 255) + "," + (n & 255) + "," + a + ")";
    }

    // Per-bloodline silhouette accents. Pure decoration anchored on the same
    // joints as the body, so they track every pose for free — and they are what
    // keeps twelve fighters readable when they all share one skeleton.
    // Render-only: reads sim.tick for flicker, writes nothing.
    function drawAccent(def, j, facing) {
        const col = def.color, col2 = def.color2;
        const hx = j.head.x, hy = -j.head.y;      // canvas space (y down)
        const R = LIMB.headR;
        const t = sim ? sim.tick : 0;
        ctx.save();
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = hexRGBA(col, 0.9);
        ctx.fillStyle = hexRGBA(col, 0.55);
        ctx.shadowColor = col; ctx.shadowBlur = 10;
        ctx.lineWidth = 3;
        switch (def.key) {
            case "oni": {      // forged horns
                ctx.fillStyle = hexRGBA(col2, 0.95);
                for (const sgn of [-1, 1]) {
                    ctx.beginPath();
                    ctx.moveTo(hx + sgn * 8, hy - R + 5);
                    ctx.lineTo(hx + sgn * 14, hy - R - 11);
                    ctx.lineTo(hx + sgn * 3, hy - R + 2);
                    ctx.closePath(); ctx.fill();
                }
                break;
            }
            case "raiju": {    // storm tufts + a static-arc tail
                ctx.beginPath();
                ctx.moveTo(hx - facing * 5, hy - R + 2); ctx.lineTo(hx - facing * 19, hy - R - 7);
                ctx.moveTo(hx - facing * 1, hy - R - 3); ctx.lineTo(hx - facing * 13, hy - R - 15);
                ctx.stroke();
                const tx = j.hip.x - facing * 14, ty = -j.hip.y + 2;
                ctx.beginPath();
                ctx.moveTo(tx, ty);
                ctx.lineTo(tx - facing * 12, ty - 8);
                ctx.lineTo(tx - facing * 8, ty - 16);
                ctx.lineTo(tx - facing * 22, ty - 24);
                ctx.stroke();
                break;
            }
            case "yukionna": { // hair like a snow veil, falling past the back
                ctx.fillStyle = hexRGBA(col, 0.30);
                ctx.strokeStyle = hexRGBA(col2, 0.75);
                const sway = Math.sin(t * 0.05) * 4;
                ctx.beginPath();
                ctx.moveTo(hx - facing * 2, hy - R + 2);
                ctx.quadraticCurveTo(hx - facing * 26 + sway, hy + 18, hx - facing * 20 + sway, -j.hip.y + 6);
                ctx.quadraticCurveTo(hx - facing * 10, hy + 22, hx - facing * 6, hy + R - 4);
                ctx.closePath(); ctx.fill(); ctx.stroke();
                break;
            }
            case "kitsune": {  // three tails fanning out behind the hip
                ctx.strokeStyle = hexRGBA(col, 0.85);
                ctx.lineWidth = 4;
                const bx = j.hip.x - facing * 10, by = -j.hip.y + 4;
                for (let i = 0; i < 3; i++) {
                    const lift = 10 + i * 14 + Math.sin(t * 0.06 + i) * 3;
                    ctx.beginPath();
                    ctx.moveTo(bx, by);
                    ctx.quadraticCurveTo(bx - facing * 26, by - lift, bx - facing * 44, by - lift + 6);
                    ctx.stroke();
                }
                break;
            }
            case "kodama": {   // two young sprigs, budding off the crown
                ctx.strokeStyle = hexRGBA(col2, 0.9);
                ctx.beginPath();
                ctx.moveTo(hx - 4, hy - R + 2); ctx.lineTo(hx - 8, hy - R - 10);
                ctx.moveTo(hx + 5, hy - R + 2); ctx.lineTo(hx + 10, hy - R - 7);
                ctx.stroke();
                ctx.fillStyle = hexRGBA(col, 0.9);
                ctx.beginPath(); ctx.ellipse(hx - 9, hy - R - 12, 3.4, 2.2, -0.6, 0, 6.283); ctx.fill();
                ctx.beginPath(); ctx.ellipse(hx + 11, hy - R - 9, 3.0, 2.0, 0.5, 0, 6.283); ctx.fill();
                break;
            }
            case "kappa": {    // the dish, and the shell across the back
                ctx.strokeStyle = hexRGBA(col2, 0.9);
                ctx.beginPath(); ctx.ellipse(hx, hy - R + 1, 10, 3.6, 0, 0, 6.283); ctx.stroke();
                ctx.strokeStyle = hexRGBA(col, 0.7);
                ctx.lineWidth = 5;
                const cx2 = (j.hip.x + j.chest.x) / 2 - facing * 16;
                const cy2 = (-j.hip.y + -j.chest.y) / 2;
                ctx.beginPath(); ctx.arc(cx2, cy2, 20, Math.PI * 0.75, Math.PI * 1.55, facing < 0); ctx.stroke();
                break;
            }
            case "ameonna": {  // her own private rainfall
                ctx.strokeStyle = hexRGBA(col2, 0.65);
                ctx.lineWidth = 2;
                for (let i = 0; i < 3; i++) {
                    const dx = hx - 14 + i * 14 + Math.sin(t * 0.11 + i * 2.1) * 2;
                    const dy = hy - R - 20 + ((t * 1.4 + i * 9) % 14);
                    ctx.beginPath(); ctx.moveTo(dx, dy); ctx.lineTo(dx - 1.5, dy + 6); ctx.stroke();
                }
                break;
            }
            case "hitodama": { // the soul-flame trailing off the skull
                ctx.strokeStyle = hexRGBA(col2, 0.85);
                ctx.lineWidth = 4;
                const flick = Math.sin(t * 0.21) * 4;
                ctx.beginPath();
                ctx.moveTo(hx - facing * 4, hy - R + 3);
                ctx.quadraticCurveTo(hx - facing * 16, hy - R - 8 + flick, hx - facing * 28, hy - R - 4 - flick);
                ctx.stroke();
                ctx.fillStyle = hexRGBA(col, 0.6);
                ctx.beginPath(); ctx.arc(hx - facing * 30, hy - R - 5 - flick, 3.2, 0, 6.283); ctx.fill();
                break;
            }
            case "tsukiusagi": { // two long moon-rabbit ears
                ctx.strokeStyle = hexRGBA(col2, 0.9);
                ctx.lineWidth = 4;
                ctx.beginPath();
                ctx.moveTo(hx - 5, hy - R + 3); ctx.quadraticCurveTo(hx - 10, hy - R - 16, hx - 6, hy - R - 26);
                ctx.moveTo(hx + 5, hy - R + 3); ctx.quadraticCurveTo(hx + 12, hy - R - 14, hx + 9, hy - R - 24);
                ctx.stroke();
                break;
            }
            case "bakeneko": { // pricked ears and the tell-tale second tail
                ctx.fillStyle = hexRGBA(col2, 0.9);
                for (const sgn of [-1, 1]) {
                    ctx.beginPath();
                    ctx.moveTo(hx + sgn * 5, hy - R + 4);
                    ctx.lineTo(hx + sgn * 11, hy - R - 9);
                    ctx.lineTo(hx + sgn * 1, hy - R + 1);
                    ctx.closePath(); ctx.fill();
                }
                ctx.strokeStyle = hexRGBA(col, 0.85);
                ctx.lineWidth = 3.5;
                const bx = j.hip.x - facing * 12, by = -j.hip.y + 4;
                for (const k of [0, 1]) {
                    const lift = 16 + k * 12 + Math.sin(t * 0.07 + k * 2) * 3;
                    ctx.beginPath();
                    ctx.moveTo(bx, by);
                    ctx.quadraticCurveTo(bx - facing * 20, by - lift, bx - facing * 34, by - lift + 10);
                    ctx.stroke();
                }
                break;
            }
            case "tengu": {    // the nose, and folded wings behind the shoulders
                ctx.strokeStyle = hexRGBA(col2, 0.95);
                ctx.lineWidth = 4;
                ctx.beginPath();
                ctx.moveTo(hx + facing * (R - 4), hy);
                ctx.lineTo(hx + facing * (R + 12), hy + 2);
                ctx.stroke();
                ctx.strokeStyle = hexRGBA(col, 0.7);
                ctx.lineWidth = 3;
                const wx = j.chest.x - facing * 8, wy = -j.chest.y;
                for (const k of [0, 1]) {
                    ctx.beginPath();
                    ctx.moveTo(wx, wy + k * 5);
                    ctx.quadraticCurveTo(wx - facing * 24, wy - 14 + k * 8, wx - facing * 40, wy - 4 + k * 10);
                    ctx.stroke();
                }
                break;
            }
            case "ryu": {      // antler crown and whiskers
                ctx.strokeStyle = hexRGBA(col2, 0.95);
                ctx.beginPath();
                ctx.moveTo(hx - 6, hy - R + 3); ctx.lineTo(hx - 10, hy - R - 10); ctx.lineTo(hx - 16, hy - R - 14);
                ctx.moveTo(hx - 10, hy - R - 10); ctx.lineTo(hx - 6, hy - R - 18);
                ctx.moveTo(hx + 6, hy - R + 3); ctx.lineTo(hx + 10, hy - R - 10); ctx.lineTo(hx + 16, hy - R - 14);
                ctx.moveTo(hx + 10, hy - R - 10); ctx.lineTo(hx + 6, hy - R - 18);
                ctx.stroke();
                ctx.strokeStyle = hexRGBA(col, 0.7);
                ctx.lineWidth = 2;
                const sway = Math.sin(t * 0.04) * 3;
                ctx.beginPath();
                ctx.moveTo(hx + facing * (R - 2), hy + 4);
                ctx.quadraticCurveTo(hx + facing * (R + 14), hy + 10 + sway, hx + facing * (R + 24), hy + 6 + sway);
                ctx.moveTo(hx + facing * (R - 2), hy + 7);
                ctx.quadraticCurveTo(hx + facing * (R + 12), hy + 16 - sway, hx + facing * (R + 20), hy + 14 - sway);
                ctx.stroke();
                break;
            }
        }
        ctx.restore();
    }

    function drawFighter(pl, def, alpha, ghost) {
        const p = ghost ? ghost.p : poseFor(pl, def);
        const facing = ghost ? ghost.facing : pl.facing;
        const wx = (ghost ? ghost.x : pl.x) / S;
        const wy = (ghost ? ghost.y : pl.y) / S;
        const j = joints(p, facing);

        ctx.save();
        ctx.translate(wx, -wy);

        const col = def.color, col2 = def.color2;

        // aura — swells with meter and (for Raiju) charge
        if (!ghost) {
            const heat = Math.min(1, pl.meter / MAX_METER * 0.7 + pl.charge / MAX_CHARGE * 0.5);
            if (heat > 0.05) {
                const g = ctx.createRadialGradient(0, 70, 8, 0, 70, 110 + heat * 40);
                g.addColorStop(0, hexRGBA(col, 0.20 * heat));
                g.addColorStop(1, hexRGBA(col, 0));
                ctx.fillStyle = g;
                ctx.beginPath(); ctx.arc(0, 70, 150, 0, 6.283); ctx.fill();
            }
        }

        ctx.globalAlpha = alpha;

        const stroke = (pts, w1, w2) => {
            for (let i = 0; i < pts.length - 1; i++) {
                const a = pts[i], b = pts[i + 1];
                ctx.beginPath();
                ctx.lineWidth = i === 0 ? w1 : w2;
                ctx.lineCap = "round";
                ctx.moveTo(a.x, -a.y); ctx.lineTo(b.x, -b.y);
                ctx.stroke();
            }
        };

        // ---- ink body ----
        // A black silhouette on a black stage is invisible, so the mass is laid
        // down twice: a wide clan-coloured glow underneath, then the ink core on
        // top. What reads is a dark body wrapped in its own light.
        const hipW = 18, chW = 24;
        const torso = () => {
            ctx.beginPath();
            ctx.moveTo(j.hip.x - hipW, -j.hip.y);
            ctx.lineTo(j.chest.x - chW, -j.chest.y);
            ctx.lineTo(j.chest.x + chW, -j.chest.y);
            ctx.lineTo(j.hip.x + hipW, -j.hip.y);
            ctx.closePath();
        };

        if (!ghost) {
            ctx.save();
            ctx.strokeStyle = hexRGBA(col, 0.45);
            ctx.fillStyle = hexRGBA(col, 0.45);
            ctx.shadowColor = col; ctx.shadowBlur = 22;
            ctx.lineJoin = "round";
            stroke(j.legB, 27, 22); stroke(j.armB, 23, 19);
            torso(); ctx.fill();
            stroke(j.legF, 29, 23);
            ctx.beginPath(); ctx.arc(j.head.x, -j.head.y, LIMB.headR + 4, 0, 6.283); ctx.fill();
            stroke(j.armF, 24, 20);
            ctx.restore();
        }

        ctx.strokeStyle = ghost ? hexRGBA(col, 0.30) : "#08080e";
        ctx.fillStyle = ghost ? hexRGBA(col, 0.22) : "#08080e";

        stroke(j.legB, 21, 16);
        stroke(j.armB, 17, 13);
        torso(); ctx.fill();
        stroke(j.legF, 23, 17);
        ctx.beginPath(); ctx.arc(j.head.x, -j.head.y, LIMB.headR, 0, 6.283); ctx.fill();
        stroke(j.armF, 18, 14);

        // ---- rim light in clan colour ----
        ctx.shadowColor = col; ctx.shadowBlur = ghost ? 6 : 14;
        ctx.strokeStyle = hexRGBA(col, ghost ? 0.35 : 0.95);
        ctx.lineWidth = 3;
        const rim = (pts) => { ctx.beginPath(); ctx.moveTo(pts[0].x, -pts[0].y); for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, -pts[i].y); ctx.stroke(); };
        rim(j.legF); rim(j.armF);
        ctx.beginPath(); ctx.arc(j.head.x, -j.head.y, LIMB.headR, 0, 6.283); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(j.hip.x + hipW * facing, -j.hip.y);
        ctx.lineTo(j.chest.x + chW * facing, -j.chest.y);
        ctx.stroke();

        // ---- bloodline accent (horns, tails, ears — the clan's silhouette) ----
        if (!ghost) drawAccent(def, j, facing);

        // ---- eyes ----
        if (!ghost) {
            ctx.shadowBlur = 16;
            ctx.fillStyle = col2;
            const ex = j.head.x + 6 * facing, ey = -j.head.y - 2;
            ctx.beginPath(); ctx.ellipse(ex, ey, 4.2, 2.4, 0, 0, 6.283); ctx.fill();
            ctx.beginPath(); ctx.ellipse(ex - 9 * facing, ey + 1, 3.4, 2.1, 0, 0, 6.283); ctx.fill();
        }
        ctx.shadowBlur = 0;

        // ---- attack arc: brush stroke through the active hitbox ----
        if (!ghost) {
            const hb = Sim.hitbox(pl);
            if (hb) {
                const cxp = (hb.x + hb.w / 2) / S - wx;
                const cyp = -((hb.y + hb.h / 2) / S - wy);
                ctx.save();
                ctx.globalAlpha = alpha * 0.85;
                ctx.strokeStyle = col2;
                ctx.shadowColor = col; ctx.shadowBlur = 22;
                ctx.lineWidth = 5; ctx.lineCap = "round";
                const r = hb.h / S / 1.5;
                ctx.beginPath();
                ctx.arc(cxp - 18 * facing, cyp, r, -1.1 * facing + (facing > 0 ? 0 : Math.PI), 1.1 * facing + (facing > 0 ? 0 : Math.PI), facing < 0);
                ctx.stroke();
                ctx.lineWidth = 2; ctx.globalAlpha = alpha * 0.5;
                ctx.beginPath();
                ctx.arc(cxp - 26 * facing, cyp, r + 9, -0.9 * facing + (facing > 0 ? 0 : Math.PI), 0.9 * facing + (facing > 0 ? 0 : Math.PI), facing < 0);
                ctx.stroke();
                ctx.restore();
            }
        }

        // ---- guard shimmer ----
        if (!ghost && (pl.state === ST.BLOCK || pl.state === ST.BLOCK_LOW) && pl.stun > 0) {
            ctx.strokeStyle = hexRGBA("#9BE8E0", 0.8);
            ctx.shadowColor = "#9BE8E0"; ctx.shadowBlur = 18; ctx.lineWidth = 3;
            ctx.beginPath(); ctx.arc(14 * facing, -74, 46, -1.2, 1.2); ctx.stroke();
            ctx.shadowBlur = 0;
        }

        ctx.restore();
    }

    // Rain over the ward. Purely cosmetic and derived from the sim tick, so it
    // never desyncs and costs nothing to roll back. Two passes at different
    // depths (behind the fighters, and in front of them) sell the distance.
    function drawRain(camX, tick, near) {
        const n = near ? 34 : 78;
        const spd = near ? 30 : 16;
        const len = near ? 30 : 15;
        const slant = near ? 7 : 4;
        const span = VH + 220;
        ctx.save();
        ctx.strokeStyle = near ? "rgba(186,228,238,0.26)" : "rgba(150,200,215,0.13)";
        ctx.lineWidth = near ? 1.6 : 1;
        ctx.beginPath();
        for (let i = 0; i < n; i++) {
            const seed = (i * 2654435761) >>> 0;
            const drift = camX * (near ? 0.85 : 0.35);
            const x = (((seed % 1900) - drift) % (VW + 140) + VW + 140) % (VW + 140) - 70;
            const y = (((seed >>> 9) % span) + tick * spd) % span - 110;
            ctx.moveTo(x, y);
            ctx.lineTo(x - slant, y + len);
        }
        ctx.stroke();
        ctx.restore();
    }

    // Contact pool. The street is almost black and the fighters are the only real
    // light source, so a dark shadow would be invisible — what grounds them here
    // is the light they SPILL. A tight core of their own colour on the wet asphalt,
    // plus a faint occlusion ring so the feet still bite into the ground. Both
    // spread and fade as the fighter rises, so height always reads.
    function drawGroundShadow(pl, def) {
        const x = pl.x / S;
        const h = Math.max(0, pl.y / S);
        const t = Math.min(1, h / 170);
        const rx = 34 + t * 26;
        const ry = 8 + t * 5;

        ctx.save();
        // occlusion: only meaningful right under a grounded fighter
        ctx.globalAlpha = 0.5 * (1 - t);
        ctx.fillStyle = "#000008";
        ctx.beginPath();
        ctx.ellipse(x, 0, 26 * (1 - t * 0.5), 6 * (1 - t * 0.5), 0, 0, 6.283);
        ctx.fill();

        // light spill, additive so it reads on black
        ctx.globalCompositeOperation = "lighter";
        ctx.globalAlpha = 0.5 * (1 - t * 0.72);
        const g = ctx.createRadialGradient(x, 0, 0, x, 0, rx);
        g.addColorStop(0, hexRGBA(def.color2, 0.55));
        g.addColorStop(0.45, hexRGBA(def.color, 0.28));
        g.addColorStop(1, hexRGBA(def.color, 0));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.ellipse(x, 0, rx, ry, 0, 0, 6.283);
        ctx.fill();
        ctx.restore();
    }

    // Darkens the corners so the eye stays on the fight.
    function drawVignette() {
        const g = ctx.createRadialGradient(VW / 2, VH * 0.46, VH * 0.34, VW / 2, VH * 0.46, VH * 0.98);
        g.addColorStop(0, "rgba(0,0,0,0)");
        g.addColorStop(1, "rgba(0,0,0,0.58)");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, VW, VH);
    }

    function drawStage(camX, tick) {
        const g = ctx.createLinearGradient(0, 0, 0, VH);
        g.addColorStop(0, "#04040a");
        g.addColorStop(0.55, "#0a0a14");
        g.addColorStop(1, "#101018");
        ctx.fillStyle = g; ctx.fillRect(0, 0, VW, VH);

        // moon
        ctx.save();
        ctx.globalAlpha = 0.5;
        const mg = ctx.createRadialGradient(VW * 0.74, 120, 10, VW * 0.74, 120, 190);
        mg.addColorStop(0, "rgba(216,180,90,0.55)");
        mg.addColorStop(1, "rgba(216,180,90,0)");
        ctx.fillStyle = mg; ctx.beginPath(); ctx.arc(VW * 0.74, 120, 190, 0, 6.283); ctx.fill();
        ctx.fillStyle = "rgba(232,205,150,0.85)";
        ctx.beginPath(); ctx.arc(VW * 0.74, 120, 52, 0, 6.283); ctx.fill();
        ctx.restore();

        // ward skyline — parallax silhouettes, deterministic by index
        const layers = [{ k: 0.25, h: 210, c: "#0b0b16", n: 18 }, { k: 0.5, h: 150, c: "#08080f", n: 26 }];
        for (const L of layers) {
            ctx.fillStyle = L.c;
            for (let i = 0; i < L.n; i++) {
                const seedw = (i * 2654435761) >>> 0;
                const w = 60 + (seedw % 70);
                const hh = L.h - ((seedw >>> 8) % 90);
                let x = (i * 118 - camX * L.k) % (L.n * 118);
                if (x < -160) x += L.n * 118;
                ctx.fillRect(x, GROUND_PY - hh, w, hh);
                // window slits
                ctx.fillStyle = "rgba(159,217,138,0.10)";
                for (let wy = 0; wy < 4; wy++) {
                    if (((seedw >>> (wy + 3)) & 3) === 0) ctx.fillRect(x + 10, GROUND_PY - hh + 14 + wy * 22, w - 20, 5);
                }
                ctx.fillStyle = L.c;
            }
        }

        // rain falling behind the fighters
        drawRain(camX, tick, false);

        // wet street
        ctx.fillStyle = "#050509";
        ctx.fillRect(0, GROUND_PY, VW, VH - GROUND_PY);

        // the moon pools on the wet asphalt — an elliptical smear, not a band,
        // so it reads as a reflection rather than a rectangle of tint
        ctx.save();
        const poolY = GROUND_PY + (VH - GROUND_PY) * 0.35;
        const mr = ctx.createRadialGradient(VW * 0.74, poolY, 4, VW * 0.74, poolY, 150);
        mr.addColorStop(0, "rgba(216,180,90,0.20)");
        mr.addColorStop(0.5, "rgba(216,180,90,0.08)");
        mr.addColorStop(1, "rgba(216,180,90,0)");
        ctx.fillStyle = mr;
        ctx.beginPath();
        ctx.ellipse(VW * 0.74, poolY, 150, (VH - GROUND_PY) * 0.62, 0, 0, 6.283);
        ctx.fill();
        ctx.restore();

        ctx.strokeStyle = "rgba(155,232,224,0.25)";
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(0, GROUND_PY); ctx.lineTo(VW, GROUND_PY); ctx.stroke();
        for (let i = 0; i < 30; i++) {
            const sx = ((i * 197 - camX * 0.9) % (VW + 200) + VW + 200) % (VW + 200) - 100;
            ctx.fillStyle = "rgba(255,255,255,0.03)";
            ctx.fillRect(sx, GROUND_PY + 12 + (i % 5) * 14, 40 + (i % 3) * 30, 2);
        }

        // rain dimpling the puddles, just past the ground line
        ctx.save();
        ctx.strokeStyle = "rgba(186,228,238,0.16)";
        ctx.lineWidth = 1;
        for (let i = 0; i < 16; i++) {
            const seed = (i * 40503) >>> 0;
            const phase = (tick + (seed % 40)) % 40;
            if (phase > 12) continue;
            const px = (((seed % 1500) - camX * 0.9) % (VW + 120) + VW + 120) % (VW + 120) - 60;
            const py = GROUND_PY + 6 + (seed >>> 7) % Math.max(1, (VH - GROUND_PY - 10));
            const r = 1 + phase * 1.6;
            ctx.globalAlpha = 0.5 * (1 - phase / 12);
            ctx.beginPath(); ctx.ellipse(px, py, r, r * 0.34, 0, 0, 6.283); ctx.stroke();
        }
        ctx.restore();
    }

    function drawFx(f) {
        const def = ROSTER[f.color] || ROSTER[0];
        const x = f.x / S, y = -f.y / S;
        const t = f.life / 18;
        if (t > 1) return;
        ctx.save();
        ctx.globalAlpha = 1 - t;
        if (f.kind === 0) {           // clean hit — ink splash
            ctx.strokeStyle = def.color2; ctx.shadowColor = def.color; ctx.shadowBlur = 20; ctx.lineWidth = 3;
            for (let i = 0; i < 7; i++) {
                const a = (i / 7) * 6.283 + f.life * 0.1;
                const r0 = 8 + t * 26, r1 = r0 + 16 + (i % 3) * 10;
                ctx.beginPath();
                ctx.moveTo(x + Math.cos(a) * r0, y + Math.sin(a) * r0);
                ctx.lineTo(x + Math.cos(a) * r1, y + Math.sin(a) * r1);
                ctx.stroke();
            }
            ctx.fillStyle = hexRGBA(def.color2, 0.5);
            ctx.beginPath(); ctx.arc(x, y, 6 + t * 30, 0, 6.283); ctx.fill();
        } else if (f.kind === 1) {    // blocked — cyan ring
            ctx.strokeStyle = "#9BE8E0"; ctx.shadowColor = "#9BE8E0"; ctx.shadowBlur = 16; ctx.lineWidth = 3;
            ctx.beginPath(); ctx.arc(x, y, 12 + t * 26, 0, 6.283); ctx.stroke();
        } else if (f.kind === 2) {    // special activation — vertical burst
            ctx.strokeStyle = def.color2; ctx.shadowColor = def.color; ctx.shadowBlur = 26; ctx.lineWidth = 4;
            for (let i = 0; i < 5; i++) {
                const ox = (i - 2) * 16;
                ctx.beginPath(); ctx.moveTo(x + ox, y + 50 - t * 90); ctx.lineTo(x + ox, y - 60 - t * 120); ctx.stroke();
            }
        } else if (f.kind === 3) {    // armor absorb — gold shockwave
            ctx.strokeStyle = "#D8B45A"; ctx.shadowColor = "#D8B45A"; ctx.shadowBlur = 20; ctx.lineWidth = 4;
            ctx.beginPath(); ctx.arc(x, y, 16 + t * 40, 0, 6.283); ctx.stroke();
        }
        ctx.restore();
    }

    function drawHUD() {
        const p1 = sim.p[0], p2 = sim.p[1];
        const d1 = Sim.def(p1), d2 = Sim.def(p2);

        const barW = 470, barH = 22, y = 34;
        const drawBar = (x, pl, def, flip) => {
            const pct = Math.max(0, pl.health) / def.health;
            ctx.fillStyle = "rgba(255,255,255,0.06)";
            ctx.fillRect(x, y, barW, barH);
            ctx.fillStyle = hexRGBA(def.color, 0.9);
            const w = barW * pct;
            ctx.fillRect(flip ? x + barW - w : x, y, w, barH);
            ctx.strokeStyle = "rgba(255,255,255,0.35)"; ctx.lineWidth = 1.5;
            ctx.strokeRect(x + 0.5, y + 0.5, barW - 1, barH - 1);

            // meter
            const mw = barW * 0.62, my = y + barH + 7;
            const mx = flip ? x + barW - mw : x;
            ctx.fillStyle = "rgba(255,255,255,0.05)"; ctx.fillRect(mx, my, mw, 7);
            const mfill = mw * (pl.meter / MAX_METER);
            ctx.fillStyle = pl.meter >= 50 ? "#D8B45A" : "rgba(216,180,90,0.5)";
            ctx.fillRect(flip ? mx + mw - mfill : mx, my, mfill, 7);

            // charge pips (Raiju)
            if (def.key === "raiju") {
                for (let i = 0; i < MAX_CHARGE; i++) {
                    const cxp = flip ? x + barW - 12 - i * 16 : x + 6 + i * 16;
                    ctx.fillStyle = i < pl.charge ? def.color2 : "rgba(255,255,255,0.12)";
                    ctx.fillRect(cxp, my + 12, 11, 5);
                }
            }

            ctx.font = "700 17px 'Space Mono', monospace";
            ctx.fillStyle = "#fff";
            ctx.textAlign = flip ? "right" : "left";
            ctx.fillText(def.name + "  " + def.kanji, flip ? x + barW : x, y - 10);

            // round pips
            for (let i = 0; i < ROUNDS_TO_WIN; i++) {
                const cxp = flip ? x + barW - 8 - i * 20 : x + 8 + i * 20;
                ctx.beginPath(); ctx.arc(cxp, y + barH + 30, 6, 0, 6.283);
                ctx.fillStyle = i < pl.wins ? def.color : "rgba(255,255,255,0.14)";
                ctx.fill();
            }
        };
        drawBar(40, p1, d1, false);
        drawBar(VW - 40 - barW, p2, d2, true);

        // timer
        ctx.textAlign = "center";
        ctx.font = "700 46px 'Space Mono', monospace";
        ctx.fillStyle = sim.timer < 600 ? "#E51E25" : "#fff";
        ctx.fillText(String(Math.ceil(sim.timer / 60)).padStart(2, "0"), VW / 2, y + 42);
        ctx.font = "600 11px 'Space Mono', monospace";
        ctx.fillStyle = "rgba(255,255,255,0.45)";
        ctx.fillText("ROUND " + sim.round, VW / 2, y + 60);

        // combo counter
        for (let i = 0; i < 2; i++) {
            const pl = sim.p[i], op = sim.p[1 - i];
            if (op.combo > 1) {
                ctx.textAlign = i === 0 ? "left" : "right";
                ctx.font = "700 34px 'Space Mono', monospace";
                ctx.fillStyle = ROSTER[pl.ci].color2;
                const cx = i === 0 ? 60 : VW - 60;
                ctx.fillText(op.combo + " HIT", cx, 190);
                ctx.font = "600 13px 'Space Mono', monospace";
                ctx.fillStyle = "rgba(255,255,255,0.6)";
                ctx.fillText(op.comboDmg + " DMG", cx, 210);
            }
        }

        // netplay telemetry — honest about what the connection is doing
        if (Net.active) {
            ctx.textAlign = "center";
            ctx.font = "600 11px 'Space Mono', monospace";
            ctx.fillStyle = "rgba(255,255,255,0.4)";
            ctx.fillText("ONLINE  " + Net.peerHandle + "   ·   " + Net.rtt + "ms   ·   " +
                Net.rollbacks + " rollbacks (max " + Net.maxSeen + "f)", VW / 2, VH - 16);
        }

        // announcement
        if (announce.life < 110 && announce.text) {
            const t = announce.life / 110;
            const a = t < 0.15 ? t / 0.15 : t > 0.8 ? (1 - t) / 0.2 : 1;
            ctx.save();
            ctx.globalAlpha = Math.max(0, Math.min(1, a));
            ctx.textAlign = "center";
            ctx.font = "700 68px 'Space Mono', monospace";
            ctx.shadowColor = "#E51E25"; ctx.shadowBlur = 30;
            ctx.fillStyle = "#fff";
            ctx.fillText(announce.text, VW / 2, VH / 2 - 20);
            if (announce.sub) {
                ctx.font = "600 22px 'Space Mono', monospace";
                ctx.shadowBlur = 14;
                ctx.fillStyle = "#D8B45A";
                ctx.fillText(announce.sub, VW / 2, VH / 2 + 18);
            }
            ctx.restore();
        }
    }

    function render() {
        if (!sim) return;
        const scale = cv.width / VW;
        ctx.setTransform(scale, 0, 0, scale, 0, 0);
        ctx.clearRect(0, 0, VW, VH);

        // camera: track midpoint, clamp to stage
        const mid = (sim.p[0].x + sim.p[1].x) / 2 / S;
        let camX = mid;
        const halfView = VW / (2 * WORLD_SCALE);
        const stagePx = STAGE_HALF / S;
        const edgeMargin = 95;   // so a cornered fighter is never clipped
        if (camX < -stagePx + halfView - edgeMargin) camX = -stagePx + halfView - edgeMargin;
        if (camX > stagePx - halfView + edgeMargin) camX = stagePx - halfView + edgeMargin;

        const shake = sim.shake > 0 ? sim.shake : 0;
        const sx = shake ? (((sim.tick * 7919) % 13) - 6) * shake * 0.12 : 0;
        const sy = shake ? (((sim.tick * 6151) % 11) - 5) * shake * 0.12 : 0;

        drawStage(camX * WORLD_SCALE, sim.tick);

        ctx.save();
        ctx.translate(VW / 2 - camX * WORLD_SCALE + sx, GROUND_PY + sy);
        ctx.scale(WORLD_SCALE, WORLD_SCALE);

        // ground reflections — origin is already the ground line, so a single flip.
        // Squashed slightly: this is a film of rainwater, not a mirror.
        ctx.save();
        ctx.globalAlpha = 0.18;
        ctx.scale(1, -0.88);
        for (let i = 0; i < 2; i++) drawFighter(sim.p[i], Sim.def(sim.p[i]), 0.5, null);
        ctx.restore();

        // Break the reflection up so it reads as wet asphalt: fade it out with
        // depth, then rake it with slow ripple bands. Without this the mirror is
        // too perfect and the fighters read as floating.
        ctx.save();
        const fade = ctx.createLinearGradient(0, 0, 0, 215);
        fade.addColorStop(0, "rgba(5,5,9,0)");
        fade.addColorStop(0.55, "rgba(5,5,9,0.55)");
        fade.addColorStop(1, "rgba(5,5,9,1)");
        ctx.fillStyle = fade;
        ctx.fillRect(-2400, 0, 4800, 215);
        ctx.globalAlpha = 0.55;
        ctx.fillStyle = "#050509";
        for (let i = 0; i < 10; i++) {
            const y = 10 + i * 21 + ((sim.tick * 0.7 + i * 9) % 13);
            ctx.fillRect(-2400, y, 4800, 2 + (i % 3));
        }
        ctx.restore();

        // contact shadows sit between the reflection and the fighters
        for (let i = 0; i < 2; i++) drawGroundShadow(sim.p[i], Sim.def(sim.p[i]));

        // splash rings sit on the water, under the fighters
        for (const sp of splashes) {
            const t = sp.life / 22;
            const x = sp.x / S;
            const r = (6 + t * 46) * sp.power;
            ctx.save();
            ctx.globalAlpha = 0.5 * (1 - t) * sp.power;
            ctx.strokeStyle = "rgba(186,228,238,0.9)";
            ctx.lineWidth = 1.6;
            ctx.beginPath();
            ctx.ellipse(x, -2, r, r * 0.3, 0, 0, 6.283);
            ctx.stroke();
            if (sp.power > 0.8) {
                ctx.globalAlpha = 0.3 * (1 - t);
                ctx.beginPath();
                ctx.ellipse(x, -2, r * 0.55, r * 0.17, 0, 0, 6.283);
                ctx.stroke();
            }
            ctx.restore();
        }

        for (let i = 0; i < 2; i++) {
            const def = Sim.def(sim.p[i]);
            for (const g of trails[i]) drawFighter(sim.p[i], def, Math.max(0, 0.34 * (1 - g.life / 14)), g);
        }
        for (let i = 0; i < 2; i++) {
            const pl = sim.p[i];
            const flash = (pl.state === ST.HITSTUN || pl.state === ST.HITSTUN_AIR) && sim.hitstop > 0;
            drawFighter(pl, Sim.def(pl), flash ? 0.75 : 1, null);
        }
        for (const f of sim.fx) drawFx(f);

        // ward walls at the stage edge
        const wall = STAGE_HALF / S;
        for (const side of [-1, 1]) {
            const g2 = ctx.createLinearGradient(side * wall, 0, side * (wall + 120), 0);
            g2.addColorStop(0, "rgba(155,232,224,0.16)");
            g2.addColorStop(1, "rgba(155,232,224,0)");
            ctx.fillStyle = g2;
            ctx.fillRect(side > 0 ? wall : -wall - 120, -330, 120, 330);
            ctx.strokeStyle = "rgba(155,232,224,0.28)";
            ctx.lineWidth = 2;
            ctx.beginPath(); ctx.moveTo(side * wall, 0); ctx.lineTo(side * wall, -330); ctx.stroke();
        }
        ctx.restore();

        // rain in front of the fight, for depth
        drawRain(camX * WORLD_SCALE, sim.tick, true);

        // the frame a big hit lands on: a short bloom that decays with hitstop
        if (sim.hitstop > 7) {
            ctx.save();
            ctx.globalAlpha = Math.min(0.22, (sim.hitstop - 7) * 0.028);
            ctx.fillStyle = "#FFF6E8";
            ctx.fillRect(0, 0, VW, VH);
            ctx.restore();
        }

        drawVignette();
        drawHUD();
    }

    // ==================================================================
    // NETPLAY — peer-to-peer rollback.
    //
    // The fight runs on both machines at once; no server simulates anything.
    // Each peer sends its own inputs and PREDICTS the opponent's (assume they
    // held what they held last). When the real input arrives and the prediction
    // was wrong, we rewind to that frame, restore the snapshot, and re-simulate
    // forward with the truth. Because Sim is a deterministic integer machine,
    // both peers land on identical state every time — that is the whole trick,
    // and it is why the engine was written the way it was.
    //
    // Signalling and matchmaking go through the Ward Board; the match itself does
    // not, so online play costs nothing to run.
    // ==================================================================

    const BOARD_API = "https://api.yamiward.com";
    const INPUT_DELAY = 2;        // frames of local delay; fewer rollbacks, tiny lag
    const MAX_ROLLBACK = 12;      // beyond this we cannot recover — stall instead
    const REDUNDANCY = 12;        // resend this many past inputs per packet
    const SYNC_TOLERANCE = 4;     // frames we may run ahead of the peer
    const RING = 32;
    const CHECKSUM_EVERY = 60;

    const Net = {
        active: false, connecting: false,
        slot: 0, matchId: null, seed: 0, peerHandle: "", peerFighter: 0,
        pc: null, dc: null,
        frame: 0,
        local: [], remote: [], used: [],
        snaps: new Array(RING),
        remoteConfirmed: -1, remoteFrame: 0, needRollback: -1, localMax: -1, peerChecks: {},
        rollbacks: 0, maxSeen: 0, stalls: 0, rtt: 0, lastPing: 0,
        desync: false, reported: false, ended: null,
        pollTimer: null, sigTimer: null, helloTimer: null, peerHello: false,
        checks: {},

        reset() {
            this.active = false; this.connecting = false;
            this.frame = 0; this.local = []; this.remote = []; this.used = [];
            this.snaps = new Array(RING);
            this.remoteConfirmed = -1; this.remoteFrame = 0; this.needRollback = -1;
            this.rollbacks = 0; this.maxSeen = 0; this.stalls = 0; this.rtt = 0;
            this.desync = false; this.reported = false; this.ended = null;
            this.peerHello = false; this.checks = {}; this.peerChecks = {};
            this.localMax = -1;
            clearInterval(this.pollTimer); clearInterval(this.sigTimer); clearInterval(this.helloTimer);
            this.pollTimer = this.sigTimer = this.helloTimer = null;
            if (this.dc) { try { this.dc.close(); } catch (e) { } this.dc = null; }
            if (this.pc) { try { this.pc.close(); } catch (e) { } this.pc = null; }
        },

        send(obj) {
            if (this.dc && this.dc.readyState === "open") {
                try { this.dc.send(JSON.stringify(obj)); } catch (e) { }
            }
        },

        localInput() { return mask1 | touchMask; },

        checksum(f) {
            let h = 5381;
            for (const v of f) h = (((h << 5) + h) ^ (v | 0)) | 0;
            return h;
        },

        saveSnap(frame) {
            this.snaps[frame % RING] = { frame: frame, data: Sim.serialize(sim) };
        },

        predict(f) {
            // assume they are still holding whatever we last saw
            for (let g = Math.min(f - 1, this.remoteConfirmed); g >= 0 && f - g < 20; g--) {
                if (this.remote[g] !== undefined) return this.remote[g];
            }
            return 0;
        },

        order(local, remote) {
            return this.slot === 0 ? [local, remote] : [remote, local];
        },

        onInputs(start, arr, theirFrame) {
            if (theirFrame > this.remoteFrame) this.remoteFrame = theirFrame;
            let earliest = -1;
            for (let k = 0; k < arr.length; k++) {
                const f = start + k;
                if (f < 0 || this.remote[f] !== undefined) continue;
                this.remote[f] = arr[k] | 0;
                if (f > this.remoteConfirmed) this.remoteConfirmed = f;
                // did we already simulate this frame with a wrong guess?
                if (f < this.frame && this.used[f] !== undefined && this.used[f] !== (arr[k] | 0)) {
                    if (earliest < 0 || f < earliest) earliest = f;
                }
            }
            if (earliest >= 0 && (this.needRollback < 0 || earliest < this.needRollback)) this.needRollback = earliest;
        },

        rollbackTo(target) {
            const snap = this.snaps[target % RING];
            if (!snap || snap.frame !== target) {
                // the frame we need has aged out of the ring — unrecoverable
                this.desync = true;
                return;
            }
            Sim.deserialize(sim, snap.data);
            const to = this.frame;
            for (let g = target; g < to; g++) {
                const li = this.local[g] || 0;
                const ri = this.remote[g] !== undefined ? this.remote[g] : this.predict(g);
                this.used[g] = ri;
                this.saveSnap(g);
                Sim.tick(sim, this.order(li, ri));
            }
            this.rollbacks++;
            const depth = to - target;
            if (depth > this.maxSeen) this.maxSeen = depth;
        },

        step() {
            if (this.desync) { this.finish("desync"); return false; }
            if (!this.dc || this.dc.readyState !== "open") { this.stalls++; return false; }

            // Every frame past remoteConfirmed was simulated on a guess. Once that
            // span reaches the rollback window we cannot repair a wrong guess any
            // more, so we wait rather than diverge.
            if (this.frame - this.remoteConfirmed > MAX_ROLLBACK) {
                this.stalls++;
                this.sendInputs();          // re-send committed frames; helps loss recovery
                return false;
            }

            const target = this.frame + INPUT_DELAY;
            this.local[target] = this.localInput();
            if (target > this.localMax) this.localMax = target;
            this.sendInputs();

            if (this.needRollback >= 0) {
                const t = Math.max(0, this.needRollback);
                this.needRollback = -1;
                this.rollbackTo(t);
                if (this.desync) { this.finish("desync"); return false; }
            }

            const li = this.local[this.frame] || 0;
            const ri = this.remote[this.frame] !== undefined ? this.remote[this.frame] : this.predict(this.frame);
            this.used[this.frame] = ri;
            this.saveSnap(this.frame);
            Sim.tick(sim, this.order(li, ri));
            this.frame++;

            // Hash a frame that is already settled — far enough back that no
            // rollback can rewrite it — so both peers hash the same thing.
            const cf = this.frame - MAX_ROLLBACK - 2;
            if (cf >= 0 && cf % CHECKSUM_EVERY === 0 && cf <= this.remoteConfirmed && this.checks[cf] === undefined) {
                const snap = this.snaps[cf % RING];
                if (snap && snap.frame === cf) {
                    const ck = this.checksum(snap.data);
                    this.checks[cf] = ck;
                    this.send({ t: "ck", f: cf, c: ck });
                    this.compareCheck(cf);
                }
            }

            if (sim.roundState === RS.MATCH_END && !this.reported) this.report();
            return true;
        },

        // Only ever send frames we have actually decided. Transmitting a frame
        // we have not reached yet would arrive as authoritative zero input and
        // permanently poison the peer's copy of our decisions.
        sendInputs() {
            if (this.localMax < 0) return;
            const start = Math.max(0, this.localMax - REDUNDANCY + 1);
            const arr = [];
            for (let f = start; f <= this.localMax; f++) arr.push(this.local[f] || 0);
            this.send({ t: "i", s: start, a: arr, f: this.frame, p: Date.now() });
        },

        onMessage(ev) {
            let m; try { m = JSON.parse(ev.data); } catch (e) { return; }
            if (m.t === "i") {
                this.onInputs(m.s | 0, m.a || [], m.f | 0);
                if (m.p) this.send({ t: "pong", p: m.p });
            } else if (m.t === "pong") {
                this.rtt = Math.round((Date.now() - m.p));
            } else if (m.t === "ck") {
                this.peerChecks[m.f | 0] = m.c | 0;
                this.compareCheck(m.f | 0);
            } else if (m.t === "hello") {
                this.peerHello = true;
            } else if (m.t === "bye") {
                this.finish("peer left");
            }
        },

        compareCheck(f) {
            const mine = this.checks[f], theirs = this.peerChecks[f];
            if (mine !== undefined && theirs !== undefined && mine !== theirs) this.desync = true;
        },

        report() {
            this.reported = true;
            if (!this.matchId) return;      // headless/offline session — nothing to record
            const winner = sim.winner;            // slot 0 is server slot A, so this maps 1:1
            const post = () => YW_ID.post(BOARD_API + "/api/arena/result", {
                matchId: this.matchId, winner: winner, rounds: [sim.p[0].wins, sim.p[1].wins]
            }).then((r) => r.json());
            post().then((r) => { this.ended = r; }).catch(() => {
                // one delayed retry, then an honest "unrecorded" state instead of silence
                setTimeout(() => {
                    post().then((r) => { this.ended = r; })
                        .catch(() => { this.ended = { status: "unrecorded", note: "connection lost — this match may not have been recorded" }; });
                }, 1500);
            });
        },

        finish(reason) {
            if (this.active) {
                this.send({ t: "bye" });
                this.active = false;
                netEnded(reason);
            }
            this.reset();
        }
    };

    // Identity is issued and signed by the Ward Board — see identity.js. A match
    // only moves the clan war when both fighters are distinct signed-in residents.
    function getUid() {
        const id = window.YW_ID && YW_ID.get();
        return id ? id.uid : "";
    }
    function getHandle() {
        const h = localStorage.getItem("yd_handle") || "";
        return h && h.length <= 24 ? h : "";
    }

    // ---- matchmaking + WebRTC handshake -------------------------------

    let netStatusEl = null;
    function netStatus(text, sub) {
        if (!netStatusEl) netStatusEl = document.getElementById("arena-net-status");
        if (!netStatusEl) return;
        netStatusEl.innerHTML = "";
        const h = document.createElement("div");
        h.className = "arena-net-line";
        h.textContent = text;
        netStatusEl.appendChild(h);
        if (sub) {
            const s = document.createElement("div");
            s.className = "arena-net-sub";
            s.textContent = sub;
            netStatusEl.appendChild(s);
        }
    }

    function findMatch() {
        if (Net.connecting || Net.active) return;
        Net.reset();
        Net.connecting = true;
        setScreen("net");
        netStatus("SEEKING AN OPPONENT", "The ward is being searched for someone awake.");
        let waited = 0;
        const poll = () => {
            YW_ID.post(BOARD_API + "/api/arena/queue", { fighter: pick1, handle: getHandle() })
                .then((r) => r.json()).then((r) => {
                if (!Net.connecting) return;
                if (r.status === "matched") {
                    clearInterval(Net.pollTimer); Net.pollTimer = null;
                    beginHandshake(r);
                } else {
                    waited += 1;
                    if (waited > 50) { cancelSearch("Nobody answered. The ward is quiet tonight — try the CPU, or try again later."); }
                    else netStatus("SEEKING AN OPPONENT", "Waiting " + waited + "s. Leave this open — the moment someone else queues, the fight starts.");
                }
            }).catch(() => { cancelSearch("Could not reach the ward registry."); });
        };
        poll();
        Net.pollTimer = setInterval(poll, 1000);
    }

    function cancelSearch(msg) {
        const uid = getUid();
        Net.connecting = false;
        clearInterval(Net.pollTimer); Net.pollTimer = null;
        fetch(BOARD_API + "/api/arena/cancel", {
            method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ uid: uid })
        }).catch(() => { });
        Net.reset();
        if (msg) { netStatus("NO MATCH", msg); }
        else setScreen("select");
    }

    function beginHandshake(m) {
        Net.matchId = m.matchId; Net.seed = m.seed; Net.slot = m.slot;
        Net.peerHandle = (m.them && m.them.handle) || "@ward_stray";
        // Clamp: a peer on a newer CDN copy of this file may know fighters we
        // don't yet. Falling back to a valid index beats crashing the match.
        Net.peerFighter = Math.min(ROSTER.length - 1, Math.max(0, (m.them ? m.them.fighter : 0) | 0));
        netStatus("OPPONENT FOUND", Net.peerHandle + " — " + ROSTER[Net.peerFighter].name + ". Opening a direct line…");

        const pc = new RTCPeerConnection({
            iceServers: [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }]
        });
        Net.pc = pc;

        pc.onicecandidate = (e) => {
            if (e.candidate) postSignal({ type: "ice", candidate: e.candidate.toJSON ? e.candidate.toJSON() : e.candidate });
        };
        pc.onconnectionstatechange = () => {
            if (pc.connectionState === "failed") {
                cancelSearch("Could not open a direct connection. Some networks block peer-to-peer play — try again, or fight the CPU.");
            }
        };

        const wire = (dc) => {
            Net.dc = dc;
            dc.onmessage = (ev) => Net.onMessage(ev);
            dc.onopen = () => {
                netStatus("LINE OPEN", "Syncing with " + Net.peerHandle + "…");
                Net.helloTimer = setInterval(() => {
                    Net.send({ t: "hello" });
                    if (Net.peerHello) { clearInterval(Net.helloTimer); Net.helloTimer = null; startNetMatch(); }
                }, 150);
            };
            dc.onclose = () => Net.finish("connection closed");
        };

        if (m.role === "offer") {
            const dc = pc.createDataChannel("kessen", { ordered: false, maxRetransmits: 0 });
            wire(dc);
            pc.createOffer().then((o) => pc.setLocalDescription(o)).then(() => {
                postSignal({ type: "offer", sdp: pc.localDescription.sdp });
            });
        } else {
            pc.ondatachannel = (e) => wire(e.channel);
        }

        Net.sigTimer = setInterval(() => pullSignals(pc, m.role), 700);
        pullSignals(pc, m.role);
    }

    function postSignal(payload) {
        YW_ID.post(BOARD_API + "/api/arena/signal", { matchId: Net.matchId, payload: payload }).catch(() => { });
    }

    let pendingIce = [];
    function pullSignals(pc, role) {
        YW_ID.get_(BOARD_API + "/api/arena/signal?matchId=" + encodeURIComponent(Net.matchId))
            .then((r) => r.json()).then((r) => {
                for (const s of (r.signals || [])) {
                    if (!s) continue;
                    if (s.type === "offer" && role === "answer") {
                        pc.setRemoteDescription({ type: "offer", sdp: s.sdp })
                            .then(() => pc.createAnswer())
                            .then((a) => pc.setLocalDescription(a))
                            .then(() => {
                                postSignal({ type: "answer", sdp: pc.localDescription.sdp });
                                for (const c of pendingIce) pc.addIceCandidate(c).catch(() => { });
                                pendingIce = [];
                            }).catch(() => { });
                    } else if (s.type === "answer" && role === "offer") {
                        pc.setRemoteDescription({ type: "answer", sdp: s.sdp }).then(() => {
                            for (const c of pendingIce) pc.addIceCandidate(c).catch(() => { });
                            pendingIce = [];
                        }).catch(() => { });
                    } else if (s.type === "ice") {
                        if (pc.remoteDescription) pc.addIceCandidate(s.candidate).catch(() => { });
                        else pendingIce.push(s.candidate);
                    }
                }
            }).catch(() => { });
    }

    function startNetMatch() {
        clearInterval(Net.sigTimer); Net.sigTimer = null;
        Net.connecting = false;
        Net.active = true;
        mode = "net";
        const charA = Net.slot === 0 ? pick1 : Net.peerFighter;
        const charB = Net.slot === 0 ? Net.peerFighter : pick1;
        sim = Sim.create(Net.seed, charA, charB);
        trails = [[], []]; splashes = []; wasAir = [false, false];
        matchResult = null;
        prevRound = 1; prevRS = RS.INTRO;
        announce = { text: "ROUND 1", sub: "闘 START", life: 0 };
        running = true; capture = true; acc = 0; last = 0;
        root.classList.add("arena-live");
        setScreen("fight");
        requestAnimationFrame(loop);
    }

    function netEnded(reason) {
        running = false; capture = false;
        root.classList.remove("arena-live");
        const box = document.getElementById("arena-result-body");
        if (box) {
            box.innerHTML = "";
            const h = document.createElement("h3");
            h.textContent = reason === "desync" ? "MATCH VOIDED" : "MATCH ENDED";
            box.appendChild(h);
            const p = document.createElement("p");
            p.textContent = reason === "desync"
                ? "The two machines fell out of step, so the result was thrown out. Nothing was recorded."
                : "The connection ended: " + reason + ".";
            box.appendChild(p);
        }
        setScreen("result");
    }

    // ==================================================================
    // UI SHELL — character select, results, hooks the rest of the site
    // ==================================================================

    const screens = {
        select: document.getElementById("arena-select"),
        net: document.getElementById("arena-net"),
        fight: document.getElementById("arena-stage"),
        result: document.getElementById("arena-result")
    };
    function setScreen(name) {
        Object.keys(screens).forEach((k) => { if (screens[k]) screens[k].style.display = (k === name ? "" : "none"); });
        if (name === "fight") resize();
    }

    // Your registered bloodline pre-selects your fighter, if it's in the roster.
    function myClanKey() {
        try {
            const raw = localStorage.getItem("yd_clan");
            if (!raw) return null;
            return JSON.parse(raw).key || null;
        } catch (e) { return null; }
    }

    let pick1 = 0, pick2 = 1;

    function buildSelect() {
        const wrap = document.getElementById("arena-roster");
        if (!wrap) return;
        wrap.innerHTML = "";
        const mine = myClanKey();
        ROSTER.forEach((c, i) => {
            const card = document.createElement("button");
            card.type = "button";
            card.className = "arena-fighter" + (i === pick1 ? " selected" : "");
            card.style.setProperty("--f-color", c.color);
            card.setAttribute("data-i", String(i));

            const badge = document.createElement("span");
            badge.className = "arena-f-kanji";
            badge.textContent = c.kanji;
            card.appendChild(badge);

            const nm = document.createElement("h4");
            nm.textContent = c.name;
            card.appendChild(nm);

            const arch = document.createElement("span");
            arch.className = "arena-f-arch";
            arch.textContent = c.archetype;
            card.appendChild(arch);

            const st = document.createElement("div");
            st.className = "arena-f-stats";
            ["POW", "SPD", "DEF", "KI"].forEach((k) => {
                const row = document.createElement("div");
                const lab = document.createElement("span"); lab.textContent = k;
                const bar = document.createElement("i");
                const fill = document.createElement("b");
                fill.style.width = (c.stats[k] * 10) + "%";
                bar.appendChild(fill);
                row.appendChild(lab); row.appendChild(bar);
                st.appendChild(row);
            });
            card.appendChild(st);

            const pros = document.createElement("p");
            pros.className = "arena-f-pro";
            pros.textContent = "+ " + c.strength;
            card.appendChild(pros);
            const cons = document.createElement("p");
            cons.className = "arena-f-con";
            cons.textContent = "− " + c.weakness;
            card.appendChild(cons);

            if (mine === c.key) {
                const tag = document.createElement("span");
                tag.className = "arena-f-yours";
                tag.textContent = "YOUR BLOODLINE";
                card.appendChild(tag);
                pick1 = i;
            }

            card.addEventListener("click", () => {
                pick1 = i;
                pick2 = ROSTER.length > 1 ? (i + 1) % ROSTER.length : i;
                wrap.querySelectorAll(".arena-fighter").forEach((el) => el.classList.toggle("selected", el === card));
                const vs = document.getElementById("arena-vs-label");
                if (vs) vs.textContent = ROSTER[pick1].name + "  ⚔  " + ROSTER[pick2].name;
            });
            wrap.appendChild(card);
        });
        wrap.querySelectorAll(".arena-fighter").forEach((el) => el.classList.toggle("selected", Number(el.getAttribute("data-i")) === pick1));
        pick2 = ROSTER.length > 1 ? (pick1 + 1) % ROSTER.length : pick1;
        const vs = document.getElementById("arena-vs-label");
        if (vs) vs.textContent = ROSTER[pick1].name + "  ⚔  " + ROSTER[pick2].name;
    }

    function showResult() {
        const wasNet = mode === "net";
        if (wasNet) { Net.active = false; Net.reset(); }
        endMatch();
        const box = document.getElementById("arena-result-body");
        if (box && matchResult) {
            const w = ROSTER[sim.p[matchResult.winner].ci];
            box.innerHTML = "";
            const h = document.createElement("h3");
            h.textContent = w.name + " TAKES THE WARD";
            h.style.color = w.color;
            box.appendChild(h);
            const sub = document.createElement("p");
            sub.textContent = matchResult.rounds[0] + " — " + matchResult.rounds[1] + "  ·  " + w.kanji + " bloodline";
            box.appendChild(sub);
            const note = document.createElement("p");
            note.className = "arena-note";
            if (wasNet) {
                const e = Net.ended;
                if (e && e.status === "final") {
                    const mine = Net.slot === 0 ? e.elo.a : e.elo.b;
                    note.textContent = "Recorded. Your rating is now " + mine +
                        (e.en ? ", and your clan took " + e.en + " EN from it." : ".");
                } else if (e && e.status === "void") {
                    note.textContent = "The two accounts reported different results, so nothing was recorded.";
                } else {
                    note.textContent = "Waiting for your opponent's report — a result is only recorded when both sides agree.";
                }
            } else {
                note.textContent = "Training ground result. Online matches are ranked and pay clan EN.";
            }
            box.appendChild(note);
        }
        setScreen("result");
    }

    const bind = (id, fn) => { const el = document.getElementById(id); if (el) el.addEventListener("click", fn); };
    bind("arena-start-cpu", () => {
        const sel = document.getElementById("arena-difficulty");
        startMatch(pick1, pick2, "cpu", Number(sel ? sel.value : 1));
    });
    bind("arena-start-local", () => startMatch(pick1, pick2, "local", 1));
    bind("arena-start-online", () => findMatch());
    bind("arena-net-cancel", () => cancelSearch(""));
    bind("arena-net-back", () => setScreen("select"));
    bind("arena-quit", () => {
        if (Net.active) { Net.finish("you left the ring"); return; }
        endMatch(); setScreen("select");
    });
    bind("arena-again", () => { setScreen("select"); });

    // Ranked ladder. Shows only fighters who have actually finished a match —
    // no seeded names, no placeholder ranks (board integrity rule).
    function loadLadder() {
        const box = document.getElementById("arena-ladder");
        if (!box) return;
        fetch(BOARD_API + "/api/arena/leaderboard").then((r) => r.json()).then((r) => {
            box.innerHTML = "";
            if (!r.players || !r.players.length) {
                const p = document.createElement("p");
                p.className = "arena-note";
                p.textContent = "No ranked fights yet. The first fighter to win an online match opens the ladder.";
                box.appendChild(p);
                return;
            }
            const ol = document.createElement("ol");
            ol.className = "arena-ladder-list";
            r.players.forEach((pl, i) => {
                const li = document.createElement("li");
                const rank = document.createElement("span");
                rank.className = "lb-rank";
                rank.textContent = ["🥇", "🥈", "🥉"][i] || String(i + 1).padStart(2, "0");
                const who = document.createElement("span");
                who.className = "lb-handle";
                who.textContent = pl.handle;
                const clan = document.createElement("span");
                clan.className = "arena-ladder-clan";
                if (pl.clan && window.YD_CLANS) {
                    const c = window.YD_CLANS.list.filter((x) => x.key === pl.clan)[0];
                    if (c) { clan.textContent = c.kanji; clan.style.color = c.color; }
                }
                const rec = document.createElement("span");
                rec.className = "arena-ladder-rec";
                rec.textContent = pl.wins + "W " + pl.losses + "L";
                const elo = document.createElement("span");
                elo.className = "lb-score";
                elo.textContent = pl.elo;
                li.appendChild(rank); li.appendChild(who); li.appendChild(clan);
                li.appendChild(rec); li.appendChild(elo);
                ol.appendChild(li);
            });
            box.appendChild(ol);
        }).catch(() => { });
    }

    buildSelect();
    loadLadder();
    setScreen("select");
    resize();
    window.addEventListener("beforeunload", () => { if (Net.active || Net.connecting) { Net.send({ t: "bye" }); } });

    // Expose the sim for the Phase 2 netcode layer and the balance harness.
    // Nothing on the page reads this; it is the seam rollback will attach to.
    window.YD_ARENA = {
        Sim: Sim, AI: AI, ROSTER: ROSTER, IN: IN, ST: ST, Net: Net,
        // Test seam for the rollback harness: start a netplay session against a
        // stub data channel so two peers can be run head-to-head headlessly.
        __beginNet(seed, slot, charA, charB, dc) {
            Net.reset();
            sim = Sim.create(seed, charA, charB);
            Net.active = true; Net.slot = slot; Net.dc = dc; Net.matchId = null;
            return sim;
        },
        __sim() { return sim; },
        // Test seam for the render layer: lets a headless harness drive the
        // purely-cosmetic passes against a recording context and assert they
        // neither throw nor emit NaN coordinates.
        __draw: { rain: drawRain, shadow: drawGroundShadow, vignette: drawVignette, stage: drawStage, accent: drawAccent, joints: joints, poseFor: poseFor },
        // Draw one frame on demand, for headless capture in the art harness.
        __render() { render(); },
        // Headless AI-vs-AI match — the balance tool. Returns winner + stats.
        simulate(a, b, seed, lvlA, lvlB) {
            const s = Sim.create(seed, a, b);
            let guard = 0;
            while (s.roundState !== RS.MATCH_END && guard++ < 60 * 60 * 12) {
                Sim.tick(s, [AI.think(s, 0, lvlA === undefined ? 2 : lvlA), AI.think(s, 1, lvlB === undefined ? 2 : lvlB)]);
            }
            return { winner: s.winner, rounds: [s.p[0].wins, s.p[1].wins], ticks: s.tick };
        }
    };
});
